/**
 * HolAVendég? — 3D turisztikai térkép.
 *
 * Ported from a claude.ai/design prototype (project 1874a8d0…, index.html) into
 * this build. Three.js scene structure, camera framing, bar/arc animation and
 * interaction model follow that design closely — see the design's index.html
 * for the reference this was adapted from.
 *
 * What changed in the port, and why:
 *
 *   - All three data fetches (tourism.json, hu-counties.topo.json via a
 *     samateja/D3topoJson gist, and jsdelivr's world-atlas countries-110m.json)
 *     are replaced with this project's own pipeline output — see
 *     src/lib/realData.ts. The design's demo blob covered a uniform 2010–2025
 *     monthly grid; real STADAT coverage is not uniform (monthly data only
 *     since 2021-01, yearly back to 2000 from a separate, differently-sourced
 *     table — see README.md "Amire figyelni kell"), so cVal/kVal read two
 *     independent matrices instead of deriving "yearly" by summing "monthly".
 *
 *   - The settlement/city layer (60 demo cities, hardcoded population figures,
 *     a proportional "share of county" allocation, and a private/business
 *     accommodation split) is removed entirely, along with the "KIMUTATÁS
 *     TÁBLA" spreadsheet built on top of it. None of that has a real KSH
 *     source in this project's pipeline — SPEC.md §4.4 marks settlement-level
 *     data as an explicit future phase — and shipping invented per-town
 *     numbers as if they were real statistics would misrepresent the one
 *     thing this site is for. County- and country-level figures below are all
 *     genuine STADAT numbers.
 *
 *   - World country geometry keys off our own world.topo.json (ISO 3166-1
 *     alpha-2 ids throughout, GB/GR already corrected — see scripts/geo.ts),
 *     so the design's ~40-entry numeric-ISO lookup table is gone; every named
 *     country resolves by `feature.id` directly.
 */
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { ascending, max, maxIndex, quantileSorted, range } from 'd3-array'
import { color } from 'd3-color'
import { geoCentroid, geoNaturalEarth1, geoPath } from 'd3-geo'
import { scaleSqrt, scaleThreshold } from 'd3-scale'
import { feature as topoFeature } from 'topojson-client'
import type { GeometryCollection, Topology } from 'topojson-specification'
import type {
  AccommodationCounts,
  CountySlug,
  IsoCode,
  PeriodKey,
  AgeDestination,
  PlaceImage,
  PlaceInfo,
  TripLength,
} from '@/types/data'
import { loadPlaces, loadTopology, resetPlacesCache } from '@/lib/dataClient'
import { loadRealData, type NamedCountry, type RealData } from '@/lib/realData'
import { initPwa } from '@/lib/pwa'
import {
  capacityFor,
  computeIndicators,
  occupancy,
  periodDays,
  SIGNAL_LABEL,
  type CountyIndicators,
} from '@/lib/indicators'

import './styles/app.css'

// ---------------------------------------------------------------------------
// bootstrap
// ---------------------------------------------------------------------------

function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id)
  if (!el) throw new Error(`Hiányzó elem: #${id}`)
  return el as T
}

function showFallback(message: string): void {
  const fb = $('fallback')
  fb.style.display = 'grid'
  const inner = fb.firstElementChild
  if (inner) inner.innerHTML = message
}

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
const RAMP = ['#F6EDDA', '#E7E3C4', '#C6D9B6', '#97C5AE', '#63ABA2', '#357F7C', '#0F5F5D']
const NODATA = '#EFEBDF'
const MUTED = '#EDE6D4'
const CORAL = '#E5735C'
const PETROL = '#0F6E6B'
const DARKMQ = matchMedia('(prefers-color-scheme: dark)')

interface ThemePalette {
  nodata: string
  muted: string
  land: string
  neutral: string
  bg: string
  ocean: string
  up: string
  down: string
}

function theme(): ThemePalette {
  return DARKMQ.matches
    ? {
        nodata: '#2B3835',
        muted: '#243230',
        land: '#1D2A28',
        neutral: '#33423E',
        bg: '#141B1A',
        ocean: '#101817',
        up: '#5FCE9F',
        down: '#F0876B',
      }
    : {
        nodata: NODATA,
        muted: MUTED,
        land: '#F0EADC',
        neutral: '#F0E9D8',
        bg: '#FAF6EF',
        ocean: '#F4EDDD',
        up: '#1D7E5F',
        down: '#C24B32',
      }
}

const nf = new Intl.NumberFormat('hu-HU')
const nf1 = new Intl.NumberFormat('hu-HU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const MONTHS = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
]

async function main(): Promise<void> {
  let data: RealData
  let huTopo: Topology
  let worldTopo: Topology

  // Before the data load, deliberately: the service worker is what serves a
  // repeat visit (and any offline one), so it should not wait on ~5MB of
  // period files — and an install offer is still valid even if the data
  // fetch below fails.
  initPwa()

  try {
    ;[data, huTopo, worldTopo] = await Promise.all([
      loadRealData(),
      loadTopology('hu-counties') as Promise<Topology>,
      loadTopology('world') as Promise<Topology>,
    ])
  } catch (e) {
    showFallback(
      `Nem sikerült betölteni az adatokat. Futott már a <code>pnpm data</code>? (${
        e instanceof Error ? e.message : String(e)
      })`,
    )
    throw e
  }

  run(data, huTopo, worldTopo)
}

// ---------------------------------------------------------------------------
// the app
// ---------------------------------------------------------------------------

/**
 * `occupancy` and `capacity` come from the supply side (KSH bed places), not
 * from the traffic tables — see src/lib/indicators.ts. Both ignore the
 * domestic/foreign selector: capacity is not split by where the guest came
 * from, and "foreign occupancy" would be a meaningless fraction.
 */
type Metric = 'nights' | 'guests' | 'stay' | 'occupancy' | 'capacity'

/** Metrics whose denominator is the whole county, so the scope switch cannot apply. */
const SCOPE_FREE_METRICS: ReadonlySet<Metric> = new Set<Metric>(['occupancy', 'capacity'])
type Scope = 'total' | 'dom' | 'for'
type View = 'stay' | 'origin'
/** Which way the traffic runs, in the origin view. */
type Flow = 'in' | 'out'
type Gran = 'm' | 'y'
type Extent = 'eu' | 'world'

interface Selection {
  type: 'county' | 'country'
  id: string
}

interface AppState {
  view: View
  /**
   * Direction of travel in the origin view. Inbound is guest-nights from the
   * accommodation register; outbound is trips from the border survey — two
   * different instruments, so every label on screen names the one in view.
   */
  flow: Flow
  metric: Metric
  scope: Scope
  gran: Gran
  mi: number
  yi: number
  ext: Extent
  arcs: boolean
  delta: boolean
  sel: Selection | null
  /** Slug of the settlement pin focused within the selected county, if any. */
  focusSettlement: string | null
}

function run(DATA: RealData, HU: Topology, WORLDJ: Topology): void {
  const C = DATA.counties
  const NC = C.length
  const K: NamedCountry[] = DATA.countries
  const NK = K.length
  const monthlyPeriods = DATA.monthlyPeriods
  const yearlyPeriods = DATA.yearlyPeriods
  const NM = monthlyPeriods.length
  const NY = yearlyPeriods.length

  const cIdx = new Map<string, number>(C.map((c, i) => [c.slug, i]))

  /*
   * Derived indicators (growth, share shift, utilisation, supply gap). Fixed
   * to a window ending at the latest complete year, so unlike everything else
   * on screen they do not move with the timeline — computing them once is
   * both correct and cheaper than recomputing per frame.
   */
  const INDICATORS = computeIndicators(DATA)
  const indicatorFor = (slug: string): CountyIndicators | undefined =>
    INDICATORS.counties.find((c) => c.slug === slug)
  const kIdx = new Map<string, number>(K.map((k, i) => [k.iso, i]))

  /**
   * Certified-accommodation counts (szallashelyminosites.hu) — a live register
   * snapshot, not a KSH time series, so unlike every other number on this
   * panel it does not change with the timeline and has no year-on-year delta.
   */
  function accommodationsFor(countySlug: string): AccommodationCounts {
    return (
      DATA.accommodationsByCounty[countySlug as CountySlug] ?? {
        total: 0,
        private: 0,
        nonPrivate: 0,
      }
    )
  }
  function nationalAccommodations(): AccommodationCounts {
    let total = 0
    let priv = 0
    let nonPriv = 0
    for (const counts of Object.values(DATA.accommodationsByCounty)) {
      total += counts.total
      priv += counts.private
      nonPriv += counts.nonPrivate
    }
    return { total, private: priv, nonPrivate: nonPriv }
  }

  // ---- series access --------------------------------------------------------

  /**
   * County value for a metric/scope at a given granularity+index. Returns null
   * — never a fabricated 0 or NaN — whenever the underlying STADAT figure is
   * genuinely absent (monthly guest counts do not exist at all: README.md
   * "Havi vendégszám nincs").
   */
  function cVal(ci: number, gran: Gran, idx: number, metric: Metric, scope: Scope): number | null {
    const src = gran === 'm' ? DATA.monthly : DATA.yearly
    const nd = src.nightsD[ci]?.[idx] ?? null
    const nfr = src.nightsF[ci]?.[idx] ?? null
    const gd = src.guestsD[ci]?.[idx] ?? null
    const gf = src.guestsF[ci]?.[idx] ?? null
    const n =
      scope === 'dom' ? nd : scope === 'for' ? nfr : nd != null && nfr != null ? nd + nfr : null
    const g =
      scope === 'dom' ? gd : scope === 'for' ? gf : gd != null && gf != null ? gd + gf : null
    if (metric === 'nights') return n
    if (metric === 'guests') return g
    if (metric === 'stay') return g != null && g > 0 && n != null ? n / g : null

    // Supply-side metrics: always the county total, never a scope slice.
    const period = (gran === 'm' ? monthlyPeriods : yearlyPeriods)[idx]
    if (!period) return null
    const beds = capacityFor(DATA.capacity, C[ci]!.slug, period)
    if (metric === 'capacity') return beds
    const total = nd != null && nfr != null ? nd + nfr : null
    return occupancy(total, beds, periodDays(period))
  }

  function natVal(gran: Gran, idx: number, metric: Metric, scope: Scope): number | null {
    if (metric === 'stay') {
      let n = 0
      let g = 0
      let any = false
      for (let ci = 0; ci < NC; ci++) {
        const nv = cVal(ci, gran, idx, 'nights', scope)
        const gv = cVal(ci, gran, idx, 'guests', scope)
        if (nv != null) {
          n += nv
          any = true
        }
        if (gv != null) g += gv
      }
      return any && g > 0 ? n / g : null
    }
    let s = 0
    let any = false
    for (let ci = 0; ci < NC; ci++) {
      const v = cVal(ci, gran, idx, metric, scope)
      if (v != null) {
        s += v
        any = true
      }
    }
    return any ? s : null
  }

  /**
   * Residents' own trips abroad, laid onto the same period grid as the
   * inbound nights so that every consumer — map, timeline, sparkline,
   * year-on-year badge — works unchanged when the direction flips.
   *
   * The survey is quarterly: a month takes its quarter's figure (the same
   * number for all three months of it, which is what "quarterly" means), and
   * a year takes the sum of its quarters. Thousands of trips, never nights.
   */
  /**
   * Which quarters of the survey a period on the timeline covers: one for a
   * month, up to four for a year, and the last published quarter for a month
   * the survey has not reached yet (it lags the calendar — the cards name the
   * quarter they are showing).
   */
  function quartersForPeriod(period: string, gran: Gran): number[] {
    const travel = DATA.travel
    if (!travel) return []
    const at = new Map(travel.quarters.map((q, i) => [q, i]))

    if (gran === 'm') {
      const key = `${period.slice(0, 4)}-Q${Math.floor((Number(period.slice(5, 7)) - 1) / 3) + 1}`
      const q =
        at.get(key) ?? (key > travel.quarters.at(-1)! ? travel.quarters.length - 1 : undefined)
      return q === undefined ? [] : [q]
    }
    return travel.quarters.map((q, i) => (q.startsWith(period) ? i : -1)).filter((i) => i >= 0)
  }

  function buildOutboundMatrix(periods: readonly string[], gran: Gran): (number | null)[][] {
    const matrix = K.map(() => periods.map(() => null as number | null))
    const travel = DATA.travel
    if (!travel) return matrix

    K.forEach((country, ki) => {
      const entry = travel.outbound.countries.find((c) => c.isos.includes(country.iso))
      if (!entry) return
      periods.forEach((period, pi) => {
        let sum: number | null = null
        for (const q of quartersForPeriod(period, gran)) {
          const value = entry.trips[q]
          if (value != null) sum = (sum ?? 0) + value
        }
        matrix[ki]![pi] = sum
      })
    })
    return matrix
  }

  const outboundMonthly = buildOutboundMatrix(monthlyPeriods, 'm')
  const outboundYearly = buildOutboundMatrix(yearlyPeriods, 'y')

  /**
   * One accessor for both directions: everything downstream — choropleth,
   * arcs, top list, KPI cards, tooltips — reads this, so flipping the
   * direction flips the whole view without a second code path.
   */
  const kVal = (ki: number, gran: Gran, idx: number): number | null => {
    if (S.flow !== 'out') {
      return (gran === 'm' ? DATA.countryMonthly : DATA.countryYearly)[ki]?.[idx] ?? null
    }
    const value = (gran === 'm' ? outboundMonthly : outboundYearly)[ki]?.[idx] ?? null
    if (value == null) return null

    // The purpose filter, when the user has narrowed it. KSH publishes the
    // purpose split for the country as a whole and the destinations
    // separately, so this is the national share applied to every
    // destination — an estimate, and labelled as one everywhere it shows:
    // the map title, the legend, the tooltips and the card that switched it
    // on all say "becsült". With every group ticked (the default) the share
    // is 1 and the map is the published figure, untouched.
    const share = purposeShare(gran, idx)
    return share == null ? value : value * share
  }

  /** Outbound is trips in thousands, inbound is nights: never the same unit. */
  const fmtFlow = (v: number | null): string =>
    S.flow === 'out'
      ? v == null
        ? 'n. a.'
        : `${nf.format(Math.round(v))} ezer út`
      : fmtShort(v, 'nights')

  // ---- state ------------------------------------------------------------

  const S: AppState = {
    view: 'stay',
    flow: 'in',
    metric: 'nights',
    scope: 'total',
    gran: 'm',
    mi: NM - 1,
    yi: NY - 1,
    ext: 'eu',
    arcs: true,
    delta: true,
    sel: null,
    focusSettlement: null,
  }

  /**
   * Where the last visit left off. The URL already carries the state, so a
   * shared link keeps winning — this only fills in when there is nothing in
   * the query string, which is what a plain reload or a bookmark to "/"
   * looks like.
   *
   * Deliberately not stored: the selected county, settlement or country. The
   * map flies to a selection on load, and coming back tomorrow to a camera
   * halfway down a county nobody remembers picking is disorienting; the view,
   * the measure and the period are the settings, the selection is a gesture.
   */
  const STATE_KEY = 'holavendeg:state'

  /** Raw "leisure,work" from the URL or storage, applied once the set exists. */
  let storedPurpose: string | null = null

  interface StoredState {
    view?: string
    flow?: string
    /** Comma-separated purpose group ids for the outbound filter. */
    purpose?: string
    metric?: string
    scope?: string
    gran?: string
    ext?: string
    period?: string
    arcs?: boolean
    delta?: boolean
  }

  function readStoredState(): StoredState {
    try {
      const raw = localStorage.getItem(STATE_KEY)
      return raw ? (JSON.parse(raw) as StoredState) : {}
    } catch {
      // Private mode, disabled storage, or something else's key on the same
      // origin: the app has a perfectly good default state to fall back on.
      return {}
    }
  }

  function writeStoredState(): void {
    try {
      localStorage.setItem(
        STATE_KEY,
        JSON.stringify({
          view: S.view,
          flow: S.flow,
          purpose: [...outSelected].sort().join(','),
          metric: S.metric,
          scope: S.scope,
          gran: S.gran,
          ext: S.ext,
          period: periodStr(),
          arcs: S.arcs,
          delta: S.delta,
        } satisfies StoredState),
      )
    } catch {
      // Storage full or blocked — the app works, it just will not remember.
    }
  }

  ;(function readInitialState() {
    const search = new URLSearchParams(location.search)
    const stored = readStoredState()
    // The URL wins wherever it says anything, so a link somebody shared opens
    // exactly as they left it, even on a browser with its own stored state.
    const q = new URLSearchParams(search)
    for (const [key, value] of Object.entries(stored)) {
      if (value !== undefined && !q.has(key)) q.set(key, String(value))
    }

    if (q.get('view') === 'origin') S.view = 'origin'
    if (q.get('flow') === 'out') S.flow = 'out'
    storedPurpose = q.get('purpose')
    if (q.get('arcs') === 'false') S.arcs = false
    if (q.get('delta') === 'false') S.delta = false
    if (['nights', 'guests', 'stay'].includes(q.get('metric') ?? ''))
      S.metric = q.get('metric') as Metric
    if (['total', 'dom', 'for'].includes(q.get('scope') ?? '')) S.scope = q.get('scope') as Scope
    if (q.get('gran') === 'y') S.gran = 'y'
    if (q.get('ext') === 'world') S.ext = 'world'
    const p = q.get('period')
    if (p) {
      const monthMatch = /^(\d{4})-(\d{2})$/.exec(p)
      const yearMatch = /^(\d{4})$/.exec(p)
      if (monthMatch && monthlyPeriods.includes(p)) {
        S.gran = 'm'
        S.mi = monthlyPeriods.indexOf(p)
      } else if (yearMatch && yearlyPeriods.includes(p)) {
        S.yi = yearlyPeriods.indexOf(p)
        if (!q.get('gran')) S.gran = 'y'
      }
    }
  })()

  const idx = (): number => (S.gran === 'm' ? S.mi : S.yi)

  function formatMonthlyPeriod(p: PeriodKey): string {
    const m = /^(\d{4})-(\d{2})$/.exec(p)
    if (!m) return p
    return `${m[1]}. ${MONTHS[Number(m[2]) - 1]}`
  }
  function formatMonthlyShort(p: PeriodKey): string {
    const m = /^(\d{4})-(\d{2})$/.exec(p)
    if (!m) return p
    return `${m[1]}. ${(MONTHS[Number(m[2]) - 1] ?? '').slice(0, 3)}.`
  }

  const periodStr = (): PeriodKey => (S.gran === 'm' ? monthlyPeriods[S.mi]! : yearlyPeriods[S.yi]!)
  const periodLabel = (): string =>
    S.gran === 'm' ? formatMonthlyPeriod(monthlyPeriods[S.mi]!) : `${yearlyPeriods[S.yi]}. év`
  const prevIdx = (): number | null =>
    S.gran === 'm' ? (S.mi >= 12 ? S.mi - 12 : null) : S.yi >= 1 ? S.yi - 1 : null

  function writeURL(): void {
    history.replaceState(
      null,
      '',
      `?${new URLSearchParams({
        view: S.view,
        flow: S.flow,
        purpose: [...outSelected].sort().join(','),
        metric: S.metric,
        scope: S.scope,
        gran: S.gran,
        ext: S.ext,
        period: periodStr(),
      }).toString()}`,
    )
    writeStoredState()
  }

  /** Switch granularity, landing on the corresponding real period where one exists. */
  function switchGranularity(g: Gran): void {
    if (S.gran === g) return
    if (g === 'y') {
      const year = monthlyPeriods[S.mi]!.slice(0, 4)
      const yi = yearlyPeriods.indexOf(year)
      S.yi = yi >= 0 ? yi : NY - 1
    } else {
      const year = yearlyPeriods[S.yi]!
      const sameYear = monthlyPeriods.map((p, i) => ({ p, i })).filter((x) => x.p.startsWith(year))
      if (sameYear.length > 0) {
        S.mi = sameYear[Math.floor(sameYear.length / 2)]!.i
      } else {
        S.mi = year < monthlyPeriods[0]!.slice(0, 4) ? 0 : NM - 1
      }
    }
    S.gran = g
  }

  // ---- formatting ---------------------------------------------------------

  function fmtShort(v: number | null, metric: Metric): string {
    if (v == null) return 'n. a.'
    if (metric === 'stay') return `${nf1.format(v)} éj`
    if (metric === 'occupancy') return `${nf1.format(v)} %`
    if (v >= 1e6) return `${nf1.format(v / 1e6)} M`
    if (v >= 1e3) return `${nf.format(Math.round(v / 1e3))} E`
    return nf.format(Math.round(v))
  }
  const fmtFull = (v: number | null, metric: Metric): string =>
    v == null
      ? 'n. a.'
      : metric === 'stay'
        ? `${nf1.format(v)} éjszaka/vendég`
        : metric === 'occupancy'
          ? `${nf1.format(v)} % férőhely-kihasználtság`
          : metric === 'capacity'
            ? `${nf.format(Math.round(v))} férőhely`
            : nf.format(Math.round(v))

  const METRIC_LABEL: Record<Metric, string> = {
    nights: 'vendégéjszaka',
    guests: 'vendég',
    stay: 'átl. tartózkodás',
    occupancy: 'kihasználtság',
    capacity: 'férőhely',
  }
  const MAP_TITLE: Record<Metric, string> = {
    nights: 'Vendégéjszakák megyénként',
    guests: 'Vendégek száma megyénként',
    stay: 'Átlagos tartózkodási idő megyénként',
    occupancy: 'Férőhely-kihasználtság megyénként',
    capacity: 'Férőhelyek megyénként',
  }
  const SCOPE_LABEL: Record<Scope, string> = {
    total: 'összes vendég',
    dom: 'belföldi vendégek',
    for: 'külföldi vendégek',
  }

  function yoyBadge(cur: number | null, prev: number | null): string {
    if (cur == null || prev == null || prev === 0) return '<span class="yoy na">év/év: n. a.</span>'
    const d = (cur / prev - 1) * 100
    const up = d >= 0
    return `<span class="yoy ${up ? 'up' : 'down'}">${up ? '▲' : '▼'} ${nf1.format(Math.abs(d))} % év/év</span>`
  }

  // ---- geo prep -------------------------------------------------------------

  const projW = geoNaturalEarth1().fitSize([2400, 1200], { type: 'Sphere' } as never)
  const HXY = projW([19.4, 47.15])!
  const pathW = geoPath(projW)
  const toXZ = (p: [number, number]): [number, number] => [p[0] - HXY[0], p[1] - HXY[1]]

  interface CountyFeature {
    type: 'Feature'
    id: string
    properties: { name: string; nuts: string }
    geometry: GeoJSON.Geometry
  }
  interface CountryFeature {
    type: 'Feature'
    id: string
    properties: { name: string }
    geometry: GeoJSON.Geometry
    a2: IsoCode | null
  }

  const countyFeatures = (
    topoFeature(HU, HU.objects['counties'] as GeometryCollection) as unknown as {
      features: CountyFeature[]
    }
  ).features
  /**
   * countyFeatures/countyMeshes/countyBars/countyLineMats are all built by
   * mapping over countyFeatures, so they share ITS order — the order
   * TopoJSON's features happen to come in, which has no reason to match
   * DATA.counties' order (that one comes from Object.entries on the
   * manifest). `cIdx` resolves a slug to a DATA.counties index; this map
   * resolves one to a countyFeatures index. Mixing the two up is exactly the
   * bug that made every county's bar show a different county's numbers.
   */
  const countyTopoIdx = new Map<string, number>(countyFeatures.map((f, i) => [f.id, i]))

  const worldFeats = (
    topoFeature(WORLDJ, WORLDJ.objects['countries'] as GeometryCollection) as unknown as {
      features: CountryFeature[]
    }
  ).features
  worldFeats.forEach((f) => {
    f.a2 = f.id
  })

  const EU_ISO = new Set<string>()
  for (const f of worldFeats) {
    if (!f.a2) continue
    const c = geoCentroid(f as unknown as GeoJSON.Feature)
    if (c[0] > -12 && c[0] < 45 && c[1] > 34 && c[1] < 72) EU_ISO.add(f.a2)
  }

  // ---- three scene ------------------------------------------------------

  const canvas = $<HTMLCanvasElement>('scene')
  let renderer: THREE.WebGLRenderer
  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true,
    })
  } catch (e) {
    showFallback('Ez a 3D nézet WebGL-t igényel, ami itt nem érhető el.')
    throw e
  }
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2))
  const scene = new THREE.Scene()
  scene.background = new THREE.Color('#FAF6EF')
  scene.fog = new THREE.Fog(0xfaf6ef, 2600, 5600)
  const camera = new THREE.PerspectiveCamera(42, innerWidth / innerHeight, 5, 9000)
  function tuneNear(): void {
    const cd = camera.position.distanceTo(controls.target)
    const nt = Math.max(0.6, Math.min(6, cd * 0.05))
    if (Math.abs(nt - camera.near) / camera.near > 0.15) {
      camera.near = nt
      camera.updateProjectionMatrix()
    }
  }
  scene.add(new THREE.HemisphereLight(0xffffff, 0xefe2cc, 1.15))
  const dl = new THREE.DirectionalLight(0xffffff, 1.5)
  dl.position.set(500, 900, 350)
  scene.add(dl)
  const dl2 = new THREE.DirectionalLight(0xfff3e0, 0.5)
  dl2.position.set(-600, 400, -500)
  scene.add(dl2)
  const ocean = new THREE.Mesh(
    new THREE.CircleGeometry(6000, 64),
    new THREE.MeshLambertMaterial({ color: '#F4EDDD' }),
  )
  ocean.rotation.x = -Math.PI / 2
  ocean.position.y = -0.4
  scene.add(ocean)

  const controls = new OrbitControls(camera, canvas)
  controls.enableDamping = true
  controls.dampingFactor = 0.08
  controls.minPolarAngle = 0.12
  controls.maxPolarAngle = 1.32
  controls.minDistance = 7
  controls.maxDistance = 3200
  controls.enablePan = true
  controls.panSpeed = 0.7

  // geometry building: project a feature's rings to XZ and extrude
  function geomToShapes(geometry: GeoJSON.Geometry): THREE.Shape[] {
    const rings: [number, number][][] = []
    let cur: [number, number][] | null = null
    const ctx = {
      beginPath() {},
      moveTo(x: number, y: number) {
        cur = [[x, y]]
      },
      lineTo(x: number, y: number) {
        cur?.push([x, y])
      },
      closePath() {
        if (cur && cur.length > 2) rings.push(cur)
        cur = null
      },
      arc() {},
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    geoPath(projW, ctx as any)({ type: 'Feature', geometry, properties: null } as never)
    const objs = rings
      .map((r) => ({
        ring: r,
        area: Math.abs(ringArea(r)),
        pts: r.map((p) => new THREE.Vector2(p[0] - HXY[0], -(p[1] - HXY[1]))),
      }))
      .filter((o) => o.area > 0.5)
    objs.sort((a, b) => b.area - a.area)
    const out: { shape: THREE.Shape; ring: [number, number][] }[] = []
    for (const o of objs) {
      let parent: { shape: THREE.Shape; ring: [number, number][] } | null = null
      for (const s of out) {
        if (ringContains(s.ring, o.ring[0]!)) {
          parent = s
          break
        }
      }
      if (parent) parent.shape.holes.push(new THREE.Path(o.pts))
      else out.push({ shape: new THREE.Shape(o.pts), ring: o.ring })
    }
    return out.map((s) => s.shape)
  }
  /** Shoelace formula — signed polygon area, matching d3.polygonArea. */
  function ringArea(ring: [number, number][]): number {
    let sum = 0
    for (let i = 0, n = ring.length; i < n; i++) {
      const [x0, y0] = ring[i]!
      const [x1, y1] = ring[(i + 1) % n]!
      sum += x0 * y1 - x1 * y0
    }
    return sum / 2
  }
  /** Point-in-polygon (ray casting), matching d3.polygonContains. */
  function ringContains(ring: [number, number][], point: [number, number]): boolean {
    const n = ring.length
    let inside = false
    const [x, y] = point
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const [xi, yi] = ring[i]!
      const [xj, yj] = ring[j]!
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
    }
    return inside
  }

  const mapRoot = new THREE.Group()
  mapRoot.rotation.x = -Math.PI / 2
  scene.add(mapRoot)

  interface RegionUserData {
    kind: 'county' | 'country' | 'land'
    id: string | null
    name: string | null
    tc: THREE.Color
    liftT?: number
  }
  interface BarUserData {
    kind: 'countyBar' | 'countryBar' | 'settlementBar'
    id: string
    name: string
    th: number
    tr: number
    r: number
    tc: THREE.Color
    toOp: number
    show?: boolean
    label?: THREE.Sprite
    /** settlementBar only — its home county, for the click-to-select and tooltip context. */
    countySlug?: string
    /** settlementBar only — real population (KSH Helységnévtár), never a tourism figure. */
    population?: number
    /** settlementBar only — the pin's full height when shown (population-scaled). */
    fullHeight?: number
    /** settlementBar only — real certified-accommodation counts (szallashelyminosites.hu). */
    accommodations?: AccommodationCounts
    /** Height when the current camera flight began — see the bar loop in tick(). */
    h0?: number
  }
  const pickables: THREE.Object3D[] = []

  function buildRegion(
    geometry: GeoJSON.Geometry,
    depth: number,
    color0: string,
    ud: Omit<RegionUserData, 'tc'>,
  ): THREE.Mesh {
    const shapes = geomToShapes(geometry)
    const geo = new THREE.ExtrudeGeometry(shapes, { depth, bevelEnabled: false })
    const mat = new THREE.MeshLambertMaterial({ color: color0 })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.userData = { ...ud, tc: new THREE.Color(color0) } satisfies RegionUserData
    mapRoot.add(mesh)
    pickables.push(mesh)
    return mesh
  }

  const countyMeshes = countyFeatures.map((f) =>
    buildRegion(f.geometry, 1.7, NODATA, { kind: 'county', id: f.id, name: f.properties.name }),
  )
  const countyLineMats: THREE.LineBasicMaterial[] = []
  const countyLineGroup = new THREE.Group()
  scene.add(countyLineGroup)
  countyFeatures.forEach((f) => {
    const mat = new THREE.LineBasicMaterial({ color: 0x0b4f4d, transparent: true, opacity: 0 })
    mat.userData = { to: 0, slug: f.id }
    countyLineMats.push(mat)
    // County shapes are always Polygon/MultiPolygon in our topology, never a
    // GeometryCollection — the cast below just tells TS what we already know.
    const geom = f.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon
    const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates
    for (const poly of polys) {
      for (const ring of poly) {
        const pts = (ring as [number, number][]).map((pt) => {
          const q = toXZ(projW(pt)!)
          return new THREE.Vector3(q[0], 1.86, q[1])
        })
        countyLineGroup.add(new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(pts), mat))
      }
    }
  })

  const countryMeshes: THREE.Mesh[] = []
  const landMeshes: THREE.Mesh[] = []
  for (const f of worldFeats) {
    const listed = f.a2 != null && kIdx.has(f.a2)
    const m = buildRegion(f.geometry, 1.0, MUTED, {
      kind: listed ? 'country' : 'land',
      id: f.a2,
      name: listed ? K[kIdx.get(f.a2!)!]!.name : null,
    })
    if (listed) countryMeshes.push(m)
    else landMeshes.push(m)
  }

  // bars
  const barGeo = new THREE.CylinderGeometry(1, 1, 1, 20)
  barGeo.translate(0, 0.5, 0)
  function makeBar(
    x: number,
    z: number,
    r: number,
    color0: string,
    ud: Omit<BarUserData, 'tc' | 'th' | 'tr' | 'r' | 'toOp'>,
  ): THREE.Mesh {
    const m = new THREE.Mesh(
      barGeo,
      new THREE.MeshLambertMaterial({ color: color0, transparent: true }),
    )
    m.position.set(x, 1.8, z)
    m.scale.set(r, 0.001, r)
    m.visible = false
    m.userData = {
      ...ud,
      th: 0,
      tr: r,
      tc: new THREE.Color(color0),
      r,
      toOp: 1,
    } satisfies BarUserData
    scene.add(m)
    pickables.push(m)
    return m
  }
  const BAR_NUDGE: Record<string, [number, number]> = { pest: [3, 2.5] }
  const countyBars = countyFeatures.map((f) => {
    const c = toXZ(pathW.centroid(f as never))
    const n = BAR_NUDGE[f.id] ?? [0, 0]
    return makeBar(c[0] + n[0], c[1] + n[1], 1.1, CORAL, {
      kind: 'countyBar',
      id: f.id,
      name: f.properties.name,
    })
  })
  const countryBars = countryMeshes.map((m) => {
    const ud = m.userData as RegionUserData
    const f = worldFeats.find((f2) => f2.a2 === ud.id)!
    const c = toXZ(pathW.centroid(f as never))
    return makeBar(c[0], c[1], 4.5, CORAL, { kind: 'countryBar', id: ud.id!, name: ud.name! })
  })

  /**
   * Settlement bubble layer: real population, real placement, no
   * per-settlement tourism figure — see src/lib/realData.ts and
   * scripts/settlements.ts for why. Bronze rather than coral on purpose: this
   * is a different kind of number from the tourism bars around it.
   */
  const SETTLEMENT_COLOR = '#B08D57'
  // Thin "pins" rather than wide bars, on purpose: population is context, not
  // the headline metric, so it should read as markers dotting the map, not
  // compete with the tourism bars for visual weight. A narrow footprint also
  // keeps Budapest (35x the next-largest settlement here) from dominating.
  const settlementPopScale = scaleSqrt()
    .domain([0, max(DATA.settlements.map((s) => s.population)) ?? 1])
    .range([0.09, 0.34])
  const settlementHeightScale = scaleSqrt()
    .domain([0, max(DATA.settlements.map((s) => s.population)) ?? 1])
    .range([0.55, 3.4])
  const settlementBars = DATA.settlements.map((s) => {
    const c = toXZ(projW([s.lon, s.lat])!)
    const bar = makeBar(c[0], c[1], settlementPopScale(s.population), SETTLEMENT_COLOR, {
      kind: 'settlementBar',
      id: s.slug,
      name: s.name,
      countySlug: s.countySlug,
      population: s.population,
      fullHeight: settlementHeightScale(s.population),
      accommodations: s.accommodations,
    })
    bar.position.y = 1.85
    return bar
  })

  /** Every animated bar, in one array — tick() and flyTo() both walk all of them. */
  const allBars = [...countyBars, ...countryBars, ...settlementBars]

  /**
   * Real municipality boundary outlines (same source as the centroid the pin
   * sits on) — hidden by default, faded in only for the currently selected
   * county's own settlements, mirroring how countyLineMats fades county
   * borders on hover/selection.
   */
  const settlementLineMats: THREE.LineBasicMaterial[] = []
  const settlementLineGroup = new THREE.Group()
  scene.add(settlementLineGroup)
  DATA.settlements.forEach((s) => {
    const mat = new THREE.LineBasicMaterial({ color: 0x0b4f4d, transparent: true, opacity: 0 })
    mat.userData = { to: 0, countySlug: s.countySlug }
    settlementLineMats.push(mat)
    const geom = s.boundary as GeoJSON.Polygon | GeoJSON.MultiPolygon
    const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates
    for (const poly of polys) {
      for (const ring of poly) {
        const pts = (ring as [number, number][]).map((pt) => {
          const q = toXZ(projW(pt)!)
          return new THREE.Vector3(q[0], 1.87, q[1])
        })
        settlementLineGroup.add(
          new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(pts), mat),
        )
      }
    }
  })

  // change labels above bars
  function makeLabel(scl: number): THREE.Sprite {
    const cv = document.createElement('canvas')
    cv.width = 256
    cv.height = 96
    const tex = new THREE.CanvasTexture(cv)
    tex.anisotropy = 4
    const sp = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, opacity: 0.5 }),
    )
    sp.scale.set(scl, scl * 0.375, 1)
    sp.visible = false
    sp.userData = { cv, tex, last: '', s0: scl }
    scene.add(sp)
    return sp
  }
  function setLabel(sp: THREE.Sprite, text: string, color0: string): void {
    if (sp.userData['last'] === `${text}|${color0}`) return
    sp.userData['last'] = `${text}|${color0}`
    const ctx = (sp.userData['cv'] as HTMLCanvasElement).getContext('2d')!
    ctx.clearRect(0, 0, 256, 96)
    if (text) {
      ctx.font = '700 42px Inter,sans-serif'
      const w = Math.min(250, ctx.measureText(text).width + 44)
      const x = (256 - w) / 2
      const dk = DARKMQ.matches
      ctx.fillStyle = dk ? 'rgba(18,26,25,.92)' : 'rgba(255,255,255,.88)'
      ctx.beginPath()
      ctx.roundRect(x, 15, w, 66, 33)
      ctx.fill()
      ctx.strokeStyle = dk ? 'rgba(255,255,255,.16)' : 'rgba(31,42,42,.12)'
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.fillStyle = color0
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, 128, 50)
    }
    ;(sp.userData['tex'] as THREE.CanvasTexture).needsUpdate = true
  }
  countyBars.forEach((b) => {
    ;(b.userData as BarUserData).label = makeLabel(5.2)
  })
  countryBars.forEach((b) => {
    ;(b.userData as BarUserData).label = makeLabel(19)
  })

  // ---- camera views -------------------------------------------------------

  function boxView(
    lon0: number,
    lat0: number,
    lon1: number,
    lat1: number,
    elev: number,
    ofs: number,
    pad: number,
  ) {
    let x0 = 1e9,
      x1 = -1e9,
      z0 = 1e9,
      z1 = -1e9
    for (let i = 0; i <= 24; i++) {
      const t = i / 24
      for (const pt of [
        [lon0 + (lon1 - lon0) * t, lat0],
        [lon0 + (lon1 - lon0) * t, lat1],
        [lon0, lat0 + (lat1 - lat0) * t],
        [lon1, lat0 + (lat1 - lat0) * t],
      ] as [number, number][]) {
        const p = projW(pt)
        if (!p) continue
        x0 = Math.min(x0, p[0])
        x1 = Math.max(x1, p[0])
        z0 = Math.min(z0, p[1])
        z1 = Math.max(z1, p[1])
      }
    }
    const w = x1 - x0,
      hh = z1 - z0
    const vFit = 2 * Math.tan(THREE.MathUtils.degToRad(21))
    const aspect = Math.max(0.75, innerWidth / innerHeight)
    const panel = innerWidth > 1060 ? ofs : 0
    const dist = Math.max(hh / vFit, w / (vFit * aspect)) * pad
    return {
      target: new THREE.Vector3((x0 + x1) / 2 - HXY[0] + dist * panel, 0, (z0 + z1) / 2 - HXY[1]),
      dist,
      elev,
      az: 0,
    }
  }
  interface CamView {
    target: THREE.Vector3
    dist: number
    elev: number
    az: number
  }
  /** Hungary's real extent in projected space — the country view frames this. */
  const huBounds = pathW.bounds({
    type: 'FeatureCollection',
    features: countyFeatures,
  } as never)

  /**
   * Screen y of the centre of the country's bounding box, for a candidate
   * view. Uses a scratch camera aimed exactly the way flyTo will aim the real
   * one — including its dist * 0.06 look-at lift — so the country view can
   * solve for its own framing before the camera has moved.
   */
  const probeCam = new THREE.PerspectiveCamera(42, 1, 5, 9000)
  function projectedCentreY(target: THREE.Vector3, dist: number, elev: number): number {
    probeCam.aspect = innerWidth / innerHeight
    probeCam.position.copy(camPosFor({ target, dist, elev, az: 0 }))
    const lookAt = target.clone()
    lookAt.y += dist * 0.06
    probeCam.lookAt(lookAt)
    probeCam.updateMatrixWorld(true)
    probeCam.updateProjectionMatrix()
    let y0 = Infinity
    let y1 = -Infinity
    for (const bx of [huBounds[0][0], huBounds[1][0]]) {
      for (const bz of [huBounds[0][1], huBounds[1][1]]) {
        const p = new THREE.Vector3(bx - HXY[0], 0, bz - HXY[1]).project(probeCam)
        const sy = ((1 - p.y) / 2) * innerHeight
        y0 = Math.min(y0, sy)
        y1 = Math.max(y1, sy)
      }
    }
    return (y0 + y1) / 2
  }

  const VIEWS: Record<'hu' | Extent, () => CamView> = {
    /*
     * Framed from the country's real bounds rather than a fixed distance, so
     * it fits whatever aspect ratio it is given — a fixed 76 was tuned for a
     * wide desktop window and left the map cropped on a tall phone.
     *
     * The x nudge is the room the desktop side rail occupies. It used to be
     * applied unconditionally, which pushed the map visibly off-centre on
     * tablets and phones, where that rail does not exist (the panel is an
     * off-canvas drawer there). boxView already guarded its own offset the
     * same way; this view did not.
     */
    hu: () => {
      const elev = 1.06
      const cx = (huBounds[0][0] + huBounds[1][0]) / 2 - HXY[0]
      const cz = (huBounds[0][1] + huBounds[1][1]) / 2 - HXY[1]

      if (innerWidth > 1060) {
        // Desktop keeps its long-standing framing: distance 76 with the map
        // nudged right of centre, which is what leaves room for the side rail
        // and the legend. Deriving it instead changed a view nobody complained
        // about, so it stays a constant.
        const dist = 76
        return { target: new THREE.Vector3(cx + dist * 0.08, 0, cz), dist, elev, az: 0 }
      }

      // Tablet/phone: no side rail to dodge, so the country sits centred — and
      // it is framed to the band actually left between the header and the
      // timeline, not the whole viewport, which those two bars can eat well
      // over a third of.
      const w = huBounds[1][0] - huBounds[0][0]
      const hh = huBounds[1][1] - huBounds[0][1]
      const vFit = 2 * Math.tan(THREE.MathUtils.degToRad(21)) // visible height per unit distance
      // Width can run close to the edges; height keeps more slack, because the
      // band is the tight axis on a landscape phone and perspective skews the
      // country's on-screen centre slightly the fuller the frame gets.
      const FILL_W = 0.8
      const FILL_H = 0.74
      const cs = getComputedStyle(document.documentElement)
      const px = (name: string, fallback: number): number =>
        parseFloat(cs.getPropertyValue(name)) || fallback
      const bandTop = px('--hud-h', 74) + px('--opt-h', 41)
      const bandBottom = innerHeight - px('--tl-h', 120)
      const bandFrac = Math.max(0.35, (bandBottom - bandTop) / innerHeight)
      // The camera looks down at `elev`, so the map's north–south extent is
      // foreshortened on screen by sin(elev); its east–west extent is not.
      const dist = Math.max(
        w / (vFit * (innerWidth / innerHeight) * FILL_W),
        (hh * Math.sin(elev)) / (vFit * bandFrac * FILL_H),
      )
      // Aim so the country lands in the middle of that band. Several things
      // pull it off centre — the band is not centred in the viewport (the
      // timeline is taller than the header), flyTo raises its look-at point
      // by dist * 0.06, and perspective spreads the near (southern) edge more
      // than the far one, so the country's on-screen box is not centred on
      // the point the camera aims at.
      //
      // Rather than model each one, aim a scratch camera exactly as flyTo
      // will, measure where the country's box actually lands, and correct.
      // Two passes: the relationship is near-linear, so the second one only
      // cleans up the remainder.
      const bandCentre = (bandTop + bandBottom) / 2
      const worldPerPx = (dist * vFit) / innerHeight
      const target = new THREE.Vector3(cx, 0, cz)
      for (let pass = 0; pass < 2; pass++) {
        // Increasing z moves the map up the screen, so a box sitting too low
        // (positive error) is corrected by increasing z.
        target.z +=
          ((projectedCentreY(target, dist, elev) - bandCentre) * worldPerPx) / Math.sin(elev)
      }
      return { target, dist, elev, az: 0 }
    },
    eu: () => boxView(-11, 34, 42, 71, 1.04, 0.1, 1.24),
    world: () => boxView(-179, -55, 179, 83, 1.22, 0.06, 1.06),
  }
  function camPosFor(v: CamView): THREE.Vector3 {
    const hor = v.dist * Math.cos(v.elev)
    return new THREE.Vector3(
      v.target.x + hor * Math.sin(v.az),
      v.dist * Math.sin(v.elev),
      v.target.z + hor * Math.cos(v.az),
    )
  }
  let camAnim: {
    t: number
    dur: number
    p0: THREE.Vector3
    p1: THREE.Vector3
    t0: THREE.Vector3
    t1: THREE.Vector3
  } | null = null
  function flyTo(v: CamView, instant = false): void {
    const tgt = v.target.clone()
    tgt.y += v.dist * 0.06
    const dest = camPosFor(v)
    if (instant || reduced) {
      camera.position.copy(dest)
      controls.target.copy(tgt)
      controls.update()
      return
    }
    camAnim = {
      t: 0,
      dur: 1.6,
      p0: camera.position.clone(),
      p1: dest,
      t0: controls.target.clone(),
      t1: tgt,
    }
    // Snapshot where each bar starts, so tick() can ease it toward its target
    // in step with this flight instead of racing ahead of the camera.
    for (const b of allBars) (b.userData as BarUserData).h0 = b.scale.y
  }
  const curView = (): CamView => (S.view === 'stay' ? VIEWS.hu() : VIEWS[S.ext]())
  function countyView(slug: string): CamView {
    const b = pathW.bounds(countyFeatures[countyTopoIdx.get(slug)!] as never)
    const w = b[1][0] - b[0][0],
      hh = b[1][1] - b[0][1]
    const vFit = 2 * Math.tan(THREE.MathUtils.degToRad(21))
    const aspect = Math.max(0.75, innerWidth / innerHeight)
    const dist = Math.max(14, Math.max(hh / vFit, w / (vFit * aspect)) * 0.72)
    return {
      target: new THREE.Vector3(
        (b[0][0] + b[1][0]) / 2 - HXY[0],
        0,
        (b[0][1] + b[1][1]) / 2 - HXY[1],
      ),
      dist,
      elev: 1.14,
      az: 0,
    }
  }
  /** Tight camera framing for a single focused settlement pin (bar already sits in toXZ/HXY-normalized space). */
  function settlementView(bar: THREE.Object3D): CamView {
    return {
      target: new THREE.Vector3(bar.position.x, 1.9, bar.position.z),
      dist: 11,
      elev: 1.18,
      az: 0,
    }
  }

  // ---- scales ---------------------------------------------------------------

  const breakCache = new Map<string, ReturnType<typeof scaleThreshold<number, string>>>()
  const barCache = new Map<string, ReturnType<typeof scaleSqrt<number, number>>>()
  function breaksFor() {
    // The direction is part of the key: thousands of outbound trips and
    // hundreds of thousands of inbound nights share no scale at all.
    const key = `${S.view}|${S.flow}|${[...outSelected].sort().join(',')}|${S.metric}|${S.scope}|${S.gran}`
    const cached = breakCache.get(key)
    if (cached) return cached
    const vals: number[] = []
    const NPER = S.gran === 'm' ? NM : NY
    if (S.view === 'stay') {
      for (let i = 0; i < NC; i++)
        for (let p = 0; p < NPER; p++) {
          const v = cVal(i, S.gran, p, S.metric, S.scope)
          if (v != null && v > 0) vals.push(v)
        }
    } else {
      for (let i = 0; i < NK; i++)
        for (let p = 0; p < NPER; p++) {
          const v = kVal(i, S.gran, p)
          if (v != null && v > 0) vals.push(v)
        }
    }
    vals.sort(ascending)
    const br = range(1, 7).map((i) => quantileSorted(vals, i / 7) ?? 0)
    const sc = scaleThreshold<number, string>().domain(br).range(RAMP)
    breakCache.set(key, sc)
    return sc
  }
  function barScale(kind: 'county' | 'country') {
    const key = `${kind}|${S.flow}|${[...outSelected].sort().join(',')}|${S.metric}|${S.scope}|${S.gran}`
    const cached = barCache.get(key)
    if (cached) return cached
    let mx = 0
    const NPER = S.gran === 'm' ? NM : NY
    if (kind === 'county') {
      for (let i = 0; i < NC; i++)
        for (let p = 0; p < NPER; p++) {
          const v = cVal(i, S.gran, p, S.metric, S.scope)
          if (v) mx = Math.max(mx, v)
        }
    } else {
      for (let i = 0; i < NK; i++)
        for (let p = 0; p < NPER; p++) {
          mx = Math.max(mx, kVal(i, S.gran, p) ?? 0)
        }
    }
    const maxH = kind === 'county' ? 17 : 100
    const sc = scaleSqrt<number, number>()
      .domain([0, mx || 1])
      .range([0, maxH])
    barCache.set(key, sc)
    return sc
  }

  // ---- arcs ---------------------------------------------------------------

  const arcGroup = new THREE.Group()
  scene.add(arcGroup)
  const pulse = new THREE.Mesh(
    new THREE.SphereGeometry(1, 12, 12),
    new THREE.MeshBasicMaterial({
      color: '#E5735C',
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    }),
  )
  pulse.visible = false
  scene.add(pulse)
  let arcSel = 0
  let arcT = 0
  function rebuildArcs(): void {
    arcGroup.children.forEach((m) => {
      const mesh = m as THREE.Mesh
      mesh.geometry.dispose()
      ;(mesh.material as THREE.Material).dispose()
    })
    arcGroup.clear()
    if (S.view !== 'origin' || !S.arcs) return
    const i = idx()
    let top = K.map((k, ki) => ({ iso: k.iso, v: kVal(ki, S.gran, i) })).filter(
      (a): a is { iso: string; v: number } => a.v != null && a.v > 0,
    )
    if (S.ext === 'world') top = top.filter((a) => !EU_ISO.has(a.iso))
    top.sort((a, b) => b.v - a.v)
    const wS = scaleSqrt()
      .domain([0, top[0]?.v ?? 1])
      .range(S.ext === 'world' ? [3, 16] : [0.45, 3.2])
    for (const a of top) {
      const f = worldFeats.find((f2) => f2.a2 === a.iso)
      if (!f) continue
      const c = toXZ(pathW.centroid(f as never))
      const abroad = new THREE.Vector3(c[0], 2, c[1])
      const home = new THREE.Vector3(4, 2, -2)
      // The pulse runs from p0 to p1, so the arc is built in the direction
      // the people move: towards Hungary for inbound, away from it for the
      // residents' own trips.
      const p0 = S.flow === 'out' ? home : abroad
      const p1 = S.flow === 'out' ? abroad : home
      const len = p0.distanceTo(p1)
      const mid = p0.clone().add(p1).multiplyScalar(0.5)
      mid.y = Math.min(S.ext === 'world' ? 560 : 300, Math.max(14, len * 0.33))
      const curve = new THREE.QuadraticBezierCurve3(p0, mid, p1)
      const tube = new THREE.Mesh(
        new THREE.TubeGeometry(curve, 48, wS(a.v), 6),
        new THREE.MeshBasicMaterial({
          color: CORAL,
          transparent: true,
          opacity: 0,
          depthWrite: false,
        }),
      )
      tube.userData = { to: 0.16, curve, w: wS(a.v), iso: a.iso }
      arcGroup.add(tube)
    }
    arcSel = 0
    arcT = 0
    pulse.visible = false
  }

  // ---- paint (sets animation targets) ---------------------------------------

  function setTargets(): void {
    const T = theme()
    const sc = breaksFor()
    const i = idx()
    const stay = S.view === 'stay'
    const hsC = barScale('county')
    const hsK = barScale('country')
    const pIdx = prevIdx()
    const dTxt = (v: number, pv: number): [string, string] => {
      const d = (v / pv - 1) * 100
      return [(d >= 0 ? '▲ ' : '▼ ') + nf1.format(Math.abs(d)) + ' %', d >= 0 ? T.up : T.down]
    }
    const kv = K.map((_k, ki) => kVal(ki, S.gran, i))
    const top10 = new Set([...kv.keys()].sort((a, b) => (kv[b] ?? -1) - (kv[a] ?? -1)).slice(0, 10))
    const selCounty = stay && S.sel?.type === 'county' ? S.sel.id : null

    countyMeshes.forEach((m, topoIdx) => {
      const ud = m.userData as RegionUserData
      // countyMeshes/countyBars are ordered by the TopoJSON feature order,
      // not by DATA.counties — cVal indexes the latter, so every lookup here
      // must go through cIdx rather than reusing this loop's own index.
      const ci = cIdx.get(ud.id!)!
      const v0 = cVal(ci, S.gran, i, S.metric, S.scope)
      if (stay) {
        if (selCounty && ud.id !== selCounty) ud.tc.set(T.muted)
        // The selected county's own terrain goes neutral, matching the
        // design: with its settlement pins now the on-screen detail layer,
        // a still-choropleth-colored floor underneath them read as clutter.
        else if (selCounty && ud.id === selCounty) ud.tc.set(T.neutral)
        else ud.tc.set(v0 == null ? T.nodata : sc(v0))
      } else ud.tc.set('#E8927D')

      const bar = countyBars[topoIdx]!
      const bud = bar.userData as BarUserData
      const v = v0
      // The selected county's own tourism column is hidden once selected —
      // its settlement pins take over as the detail layer, so the big
      // county-wide cylinder sitting on top of them just looks wrong
      // (design: `barOn = !selCounty || (id===selCounty && !drill)`, and
      // every county has at least one settlement — scripts/settlements.ts
      // fails the build otherwise — so drill is always true here once a
      // county is selected).
      const barOn = stay && !!v && !selCounty
      bud.th = barOn && v ? hsC(v) : 0
      bud.tr = v ? 0.62 + 1.05 * Math.sqrt(v / hsC.domain()[1]!) : bud.r
      bud.tc.set(
        S.sel?.id === ud.id
          ? CORAL
          : v
            ? (color(sc(v))?.darker(0.45).formatHex() ?? PETROL)
            : PETROL,
      )
      const pv = pIdx == null ? null : cVal(ci, S.gran, pIdx, S.metric, S.scope)
      bud.show = !!(barOn && S.delta && v && pv)
      if (bud.show && v && pv && bud.label) {
        const [t, c] = dTxt(v, pv)
        setLabel(bud.label, t, c)
      }
    })

    countryMeshes.forEach((m) => {
      const ud = m.userData as RegionUserData
      if (!stay) {
        const v = kVal(kIdx.get(ud.id!)!, S.gran, i)
        ud.tc.set(v != null && v > 0 ? sc(v) : T.nodata)
      } else ud.tc.set(T.muted)
    })

    countyLineMats.forEach((mt) => {
      const id = mt.userData['slug'] as string
      mt.userData['to'] = stay ? (selCounty ? (id === selCounty ? 0.95 : 0.16) : 0.3) : 0
      mt.color.set(selCounty === id ? 0xe5735c : 0x0b4f4d)
    })

    countryBars.forEach((bar) => {
      const bud = bar.userData as BarUserData
      const ki = kIdx.get(bud.id)!
      const v = kVal(ki, S.gran, i)
      bud.th = !stay && v ? hsK(v) : 0
      bud.tr = v ? 2.2 + 4.4 * Math.sqrt(v / hsK.domain()[1]!) : bud.r
      bud.tc.set(
        S.sel?.id === bud.id
          ? CORAL
          : v
            ? (color(sc(v))?.darker(0.45).formatHex() ?? PETROL)
            : PETROL,
      )
      bud.toOp = !stay && S.sel?.type === 'country' ? (bud.id === S.sel.id ? 1 : 0.22) : 1
      const pv = pIdx == null ? null : kVal(ki, S.gran, pIdx)
      bud.show = !!(!stay && S.delta && v && pv && top10.has(ki))
      if (bud.show && v && pv && bud.label) {
        const [t, c] = dTxt(v, pv)
        setLabel(bud.label, t, c)
      }
    })

    // Settlement pins + boundaries: hidden nationwide, matching the design —
    // they only appear for the selected county's own settlements, revealed
    // by clicking into a county (population never changes with the timeline,
    // so radius is fixed at creation; only visibility reacts to selection).
    settlementBars.forEach((bar) => {
      const bud = bar.userData as BarUserData
      const isSel = stay && selCounty != null && selCounty === bud.countySlug
      bud.th = isSel ? (bud.fullHeight ?? bud.r) : 0
      bud.toOp = isSel && S.focusSettlement != null ? (bud.id === S.focusSettlement ? 1 : 0.22) : 1
    })
    settlementLineMats.forEach((mt) => {
      const isSel = stay && selCounty != null && selCounty === (mt.userData['countySlug'] as string)
      mt.userData['to'] = isSel ? 0.85 : 0
    })

    rebuildArcs()
  }

  // ---- legend + panels ------------------------------------------------------

  function paintLegend(): void {
    const sc = breaksFor()
    const br = sc.domain()
    const cap =
      S.view === 'stay'
        ? METRIC_LABEL[S.metric]
        : S.flow === 'out'
          ? 'magyar utazás (ezer út)'
          : 'vendégéjszaka'
    const fm = (v: number) =>
      S.view === 'stay'
        ? fmtShort(v, S.metric)
        : S.flow === 'out'
          ? nf.format(Math.round(v))
          : fmtShort(v, 'nights')
    let html = ''
    RAMP.forEach((col, j) => {
      const lab =
        j === 0
          ? `≤ ${fm(br[0]!)}`
          : j === RAMP.length - 1
            ? `≥ ${fm(br[br.length - 1]!)}`
            : `${fm(br[j - 1]!)}–${fm(br[j]!)}`
      html += `<span class="sw"><i style="background:${col}"></i><span>${lab}</span></span>`
    })
    html += `<span class="sw" style="margin-left:6px"><i style="background:${NODATA}"></i><span>nincs adat</span></span>`
    const source =
      S.view === 'origin' && S.flow === 'out'
        ? `KSH határmegfigyelés (tur0070)${
            purposeFiltered() ? ' · az országos cél-arányból becsülve (tur0047)' : ''
          }`
        : 'KSH STADAT'
    html += `<div class="src">Oszlopmagasság és -szín: ${cap} · Forrás: ${source} · határok: Eurostat GISCO</div>`
    $('legend').innerHTML = html
  }

  function kpiCard(lab: string, val: string, yoy?: string): string {
    return `<div class="kpi"><div class="lab">${lab}</div><div class="val">${val}</div>${yoy ?? ''}</div>`
  }
  /** Muted caption for a card with no year-on-year comparison to show. */
  function liveNote(text: string): string {
    return `<span class="yoy na">${text}</span>`
  }

  /**
   * The domestic/foreign split as a shape, not just a percentage. A donut
   * rather than a pie: the hole leaves room for the headline figure, and the
   * two segments are the only two categories there are — a split this simple
   * is exactly what a circle is good at.
   *
   * Drawn with a dash pattern on one circle, so there is no arc arithmetic to
   * get wrong and nothing to fix when a share reaches 100 %.
   */
  function ratioDonut(
    domestic: number | null,
    foreign: number | null,
    metric: Metric,
    format?: (value: number) => string,
  ): string {
    if (domestic == null || foreign == null) return ''
    const total = domestic + foreign
    if (total <= 0) return ''

    const share = foreign / total
    const R = 26
    const C = 2 * Math.PI * R
    const foreignArc = share * C

    return (
      `<div class="ratio">` +
      `<svg class="ratio-donut" viewBox="0 0 64 64" aria-hidden="true">` +
      `<circle class="dom" cx="32" cy="32" r="${R}"></circle>` +
      `<circle class="for" cx="32" cy="32" r="${R}" ` +
      `stroke-dasharray="${foreignArc.toFixed(2)} ${(C - foreignArc).toFixed(2)}" ` +
      `transform="rotate(-90 32 32)"></circle>` +
      `<text x="32" y="33" class="ratio-mid">${nf1.format(share * 100)}</text>` +
      `<text x="32" y="42" class="ratio-sub">% külföldi</text>` +
      `</svg>` +
      `<div class="ratio-key">` +
      `<span><i class="dom"></i>Belföldi` +
      `<b>${format ? format(domestic) : fmtShort(domestic, metric)} · ${nf1.format((1 - share) * 100)} %</b></span>` +
      `<span><i class="for"></i>Külföldi` +
      `<b>${format ? format(foreign) : fmtShort(foreign, metric)} · ${nf1.format(share * 100)} %</b></span>` +
      `</div></div>`
    )
  }

  function renderKpis(): void {
    const i = idx()
    const pi = prevIdx()
    let html = ''
    if (S.view === 'stay') {
      const n = natVal(S.gran, i, 'nights', S.scope)
      const g = natVal(S.gran, i, 'guests', S.scope)
      const st = natVal(S.gran, i, 'stay', S.scope)
      html += kpiCard(
        'Vendégéjszakák',
        fmtShort(n, 'nights'),
        yoyBadge(n, pi == null ? null : natVal(S.gran, pi, 'nights', S.scope)),
      )
      html += kpiCard(
        'Vendégek',
        fmtShort(g, 'guests'),
        yoyBadge(g, pi == null ? null : natVal(S.gran, pi, 'guests', S.scope)),
      )
      html += kpiCard('Átl. tartózkodás', fmtShort(st, 'stay'))
      const nTot = natVal(S.gran, i, 'nights', 'total')
      const nFor = natVal(S.gran, i, 'nights', 'for')
      html += kpiCard(
        'Külföldi arány',
        nTot && nFor != null ? `${nf1.format((nFor / nTot) * 100)} %` : 'n. a.',
      )
      const acc = nationalAccommodations()
      html += kpiCard(
        'Nem magán szálláshely',
        `${nf.format(acc.nonPrivate)} db`,
        liveNote('élő nyilvántartás, nem évközi adat'),
      )
      html += kpiCard(
        'Magánszálláshely',
        `${nf.format(acc.private)} db`,
        liveNote('szallashelyminosites.hu'),
      )
      // Only for a measure that actually has the two halves: an average stay
      // or a capacity figure has no domestic/foreign split to draw.
      if (!SCOPE_FREE_METRICS.has(S.metric)) {
        html += ratioDonut(
          natVal(S.gran, i, S.metric === 'guests' ? 'guests' : 'nights', 'dom'),
          natVal(S.gran, i, S.metric === 'guests' ? 'guests' : 'nights', 'for'),
          S.metric === 'guests' ? 'guests' : 'nights',
        )
      }
      $('kpi-title').textContent = `Országos összkép · ${periodLabel()}`
    } else {
      let tot = 0
      for (let k = 0; k < NK; k++) tot += kVal(k, S.gran, i) ?? 0
      let ptot: number | null = null
      if (pi != null) {
        ptot = 0
        for (let k = 0; k < NK; k++) ptot += kVal(k, S.gran, pi) ?? 0
      }
      const out = S.flow === 'out'
      if (out) {
        // The national total for outbound is the survey's own sum over the
        // destinations shown, not a separate published figure — so it is
        // labelled as that rather than as "the country's outbound travel".
        html += kpiCard(
          'Utazás a kiemelt célországokba',
          fmtFlow(tot),
          purposeFiltered() ? liveNote('becsült, szűrt cél szerint') : undefined,
        )
      } else {
        const forN = natVal(S.gran, i, 'nights', 'for')
        html += kpiCard(
          'Külföldi vendégéjszakák',
          fmtShort(forN, 'nights'),
          yoyBadge(forN, pi == null ? null : natVal(S.gran, pi, 'nights', 'for')),
        )
        html += kpiCard('Kiemelt országokból', fmtShort(tot, 'nights'), yoyBadge(tot, ptot))
      }
      const values = K.map((_k, ki) => kVal(ki, S.gran, i) ?? -1)
      html += kpiCard(
        out ? 'Legnépszerűbb célország' : 'Legnagyobb küldő',
        K[maxIndex(values)]?.name ?? '—',
      )
      // Counted, not assumed: the two directions publish different country
      // sets, so "19 ország" was wrong the moment the map was reversed.
      const reported = K.filter((_k, ki) => kVal(ki, S.gran, i) != null).length
      html += kpiCard(out ? 'Célországok' : 'Kiemelt országok', `${reported} ország`)
      if (out) {
        const window = travelWindow()
        html += kpiCard(
          'Forrás',
          // Two short words instead of one very long compound: the card is
          // 130px wide, and "határmegfigyelés" does not fit on a line there.
          'KSH határ-<wbr>megfigyelés',
          liveNote(`${window?.label ?? ''} · utazás, nem vendégéjszaka`),
        )
      }
      $('kpi-title').textContent = out
        ? `Kiutazó forgalom · ${periodLabel()}`
        : `Beutazó forgalom · ${periodLabel()}`
    }
    $('kpis').innerHTML = html
  }

  function renderTop(): void {
    const i = idx()
    let rows: { id: string; type: 'county' | 'country'; nm: string; v: number | null }[]
    if (S.view === 'stay') {
      $('top-title').textContent = `Top 5 megye · ${METRIC_LABEL[S.metric]}`
      rows = C.map((c, ci) => ({
        id: c.slug,
        type: 'county' as const,
        nm: c.name,
        v: cVal(ci, S.gran, i, S.metric, S.scope),
      }))
    } else {
      $('top-title').textContent = S.flow === 'out' ? 'Top 5 célország' : 'Top 5 küldő ország'
      rows = K.map((k, ki) => ({
        id: k.iso,
        type: 'country' as const,
        nm: k.name,
        v: kVal(ki, S.gran, i),
      }))
    }
    const ranked = rows
      .filter((r): r is typeof r & { v: number } => r.v != null)
      .sort((a, b) => b.v - a.v)
      .slice(0, 5)
    const mx = ranked[0]?.v ?? 1
    $('toplist').innerHTML = ranked
      .map(
        (r, j) =>
          `<div class="toprow" data-type="${r.type}" data-id="${r.id}"><span class="rk">${j + 1}</span><span class="nm">${r.nm}</span><span class="vl">${
            S.view === 'stay' ? fmtShort(r.v, S.metric) : fmtFlow(r.v)
          }</span><span class="bar"><i style="width:${(r.v / mx) * 100}%"></i></span></div>`,
      )
      .join('')
    $('toplist')
      .querySelectorAll<HTMLElement>('.toprow')
      .forEach((el) => {
        el.addEventListener('click', () =>
          select({ type: el.dataset['type'] as 'county' | 'country', id: el.dataset['id']! }),
        )
      })
  }

  /**
   * What inbound visitors spend their money on, nationally. The bars are
   * shares of the published total, biggest first; the tail is folded into one
   * "egyéb" row rather than listing nineteen categories, three of which are
   * rounding error.
   *
   * National on purpose: KSH publishes this structure for all inbound travel
   * at once, never per country. Showing it under a selected country as if it
   * were that country's basket would be the same invented number this project
   * refuses everywhere else — so it is titled and captioned as the whole.
   */
  const SPEND_ROWS = 8

  function renderSpending(): void {
    const card = $('card-spend')
    const window = travelWindow()
    // Inbound money only: this is what visitors spend *here*, which has
    // nothing to say about residents' own trips abroad.
    card.hidden = S.view !== 'origin' || S.flow === 'out' || !TRAVEL || !window
    if (card.hidden || !TRAVEL || !window) return

    const rows = TRAVEL.consumption
      .map((entry) => ({ label: entry.label, value: travelSum(entry.values, window.indices) }))
      .filter((row): row is { label: string; value: number } => row.value != null && row.value > 0)
      .sort((a, b) => b.value - a.value)

    const total = travelSum(TRAVEL.consumptionTotal, window.indices)
    const shown = rows.slice(0, SPEND_ROWS)
    const rest = rows.slice(SPEND_ROWS).reduce((sum, row) => sum + row.value, 0)
    if (rest > 0) shown.push({ label: 'Egyéb kategóriák', value: rest })

    const max = shown[0]?.value ?? 1
    $('spend-title').textContent = `Mire költenek nálunk · ${window.label}`
    $('spendlist').innerHTML = shown
      .map(
        (row) =>
          `<div class="spendrow"><span class="nm">${row.label}</span>` +
          `<span class="vl">${nf.format(Math.round(row.value))} M Ft</span>` +
          `<span class="pct">${total ? `${nf1.format((row.value / total) * 100)} %` : ''}</span>` +
          `<span class="bar"><i style="width:${(row.value / max) * 100}%"></i></span></div>`,
      )
      .join('')
    $('spend-note').textContent =
      `Összesen ${total == null ? 'n. a.' : `${nf.format(Math.round(total))} millió Ft`} · ` +
      `KSH határmegfigyelés (tur0046)${window.fallback ? ' — a legutóbbi közölt negyedév' : ''} — ` +
      `országos adat, nem a kiválasztott országé.`
  }

  /**
   * Work or holiday, for residents' trips abroad.
   *
   * The three groups are KSH's own purpose rows, gathered: a business trip
   * and a posting abroad are both work, and visiting relatives is neither a
   * holiday nor a job, so it keeps its own line rather than being folded into
   * one to make the split look tidier.
   *
   * This is a card and not a filter on the map for one reason: tur0047 gives
   * the purpose split for the country as a whole, and tur0070 gives the
   * destinations. Nothing published joins the two, and applying the national
   * shares to each destination would be an invented number of exactly the
   * kind this project refuses.
   */
  const OUTBOUND_GROUPS: { id: string; label: string; keys: string[] }[] = [
    {
      id: 'leisure',
      label: 'Szabadidő, nyaralás',
      keys: ['szabadido-szorakozas-egeszsegmegorzes'],
    },
    {
      id: 'private',
      label: 'Egyéb magáncélú (rokon, vásárlás)',
      keys: [
        'rokon-barat-latogatas',
        'vasarlas',
        'egyeb-magan-celu-tanulas-gyogykezeles-vallas-vadaszat-egyeb',
      ],
    },
    {
      id: 'work',
      label: 'Munka (üzleti út, munkavégzés)',
      keys: ['uzleti-ut-konferencia-uzleteles', 'munkavegzes'],
    },
  ]

  const OUT_MEASURE_LABEL: Record<'trips' | 'days' | 'spending', string> = {
    trips: 'utazás (ezer)',
    days: 'eltöltött nap (ezer)',
    spending: 'költés (millió Ft)',
  }

  let outMeasure: 'trips' | 'days' | 'spending' = 'trips'

  /** Which purpose groups the map counts. All of them, until told otherwise. */
  const outSelected = new Set(OUTBOUND_GROUPS.map((g) => g.id))

  /**
   * The selected groups' share of all outbound trips, for the period in view.
   * null when everything is selected (nothing to scale) or when the split
   * cannot be read for that period.
   */
  function purposeShare(gran: Gran, idx: number): number | null {
    const travel = DATA.travel
    if (!travel || outSelected.size === OUTBOUND_GROUPS.length) return null

    const periods = gran === 'm' ? monthlyPeriods : yearlyPeriods
    const quarters = quartersForPeriod(periods[idx] ?? '', gran)
    if (quarters.length === 0) return null

    let selected = 0
    let total = 0
    for (const group of OUTBOUND_GROUPS) {
      for (const key of group.keys) {
        for (const length of ['oneDay', 'multiDay'] as const) {
          const entry = travel.outbound.motivations[length][key]
          if (!entry) continue
          for (const q of quarters) {
            const value = entry.trips[q]
            if (value == null) continue
            total += value
            if (outSelected.has(group.id)) selected += value
          }
        }
      }
    }
    if (total === 0) return null
    return selected / total
  }

  // A remembered filter, applied now that the set it belongs to exists. An
  // unknown or empty value is ignored rather than emptying the map.
  if (storedPurpose) {
    const wanted = storedPurpose.split(',').filter((id) => OUTBOUND_GROUPS.some((g) => g.id === id))
    if (wanted.length > 0) {
      outSelected.clear()
      for (const id of wanted) outSelected.add(id)
    }
  }

  /** True while the map is showing an estimate rather than a published figure. */
  const purposeFiltered = (): boolean =>
    S.flow === 'out' && outSelected.size < OUTBOUND_GROUPS.length

  function renderOutboundPurposes(): void {
    const card = $('card-outpurpose')
    const probe = (index: number): boolean =>
      TRAVEL?.outbound.motivations.multiDay['osszesen']?.trips[index] != null
    const window = TRAVEL ? travelWindow(probe) : null
    card.hidden = S.view !== 'origin' || S.flow !== 'out' || !TRAVEL || !window
    if (card.hidden || !TRAVEL || !window) return

    // Both trip lengths together: a day trip to work in Vienna and a week in
    // Croatia are the same question here.
    const value = (key: string): number | null => {
      let total: number | null = null
      for (const length of ['oneDay', 'multiDay'] as const) {
        const entry = TRAVEL.outbound.motivations[length][key]
        const sum = entry ? travelSum(entry[outMeasure], window.indices) : null
        if (sum != null) total = (total ?? 0) + sum
      }
      return total
    }

    const rows = OUTBOUND_GROUPS.map((group) => ({
      id: group.id,
      label: group.label,
      value: group.keys.reduce<number | null>((sum, key) => {
        const v = value(key)
        return v == null ? sum : (sum ?? 0) + v
      }, null),
    }))
    const total = value('osszesen') ?? rows.reduce((sum, r) => sum + (r.value ?? 0), 0)
    const max = Math.max(1, ...rows.map((r) => r.value ?? 0))
    const unit = outMeasure === 'spending' ? 'M Ft' : 'ezer'

    // The period lives in the note, not the heading: beside the measure
    // switch the title has room for three words, not for a date as well.
    $('op-title').textContent = 'Miért utazunk ki'
    $('oplist').innerHTML = rows
      .map(
        (row) =>
          `<label class="spendrow op-row"><span class="nm">` +
          `<input type="checkbox" data-opg="${row.id}"${outSelected.has(row.id) ? ' checked' : ''}>` +
          `${row.label}</span>` +
          `<span class="vl">${row.value == null ? 'n. a.' : `${nf.format(Math.round(row.value))} ${unit}`}</span>` +
          `<span class="pct">${
            row.value == null || !total ? '' : `${nf1.format((row.value / total) * 100)} %`
          }</span>` +
          `<span class="bar"><i style="width:${((row.value ?? 0) / max) * 100}%"></i></span></label>`,
      )
      .join('')
    $('oplist')
      .querySelectorAll<HTMLInputElement>('input[data-opg]')
      .forEach((box) => {
        box.addEventListener('change', () => {
          const id = box.dataset['opg']!
          if (box.checked) outSelected.add(id)
          else outSelected.delete(id)
          // Unticking everything would blank the map; the last one stays on.
          if (outSelected.size === 0) {
            outSelected.add(id)
            box.checked = true
          }
          update()
        })
      })
    $('op-note').textContent =
      `${window.label} · ${OUT_MEASURE_LABEL[outMeasure]} · összesen ` +
      `${total ? `${nf.format(Math.round(total))} ${unit}` : 'n. a.'} · KSH határmegfigyelés (tur0047)` +
      `${window.fallback ? ' — a legutóbbi közölt negyedév' : ''} — országos bontás.` +
      (purposeFiltered()
        ? ` A térkép a kipipált célok országos arányára van skálázva (${nf1.format(
            (purposeShare(S.gran, idx()) ?? 1) * 100,
          )} %) — ez becslés, nem közölt adat: a KSH a célt csak országosan bontja, célországonként nem.`
        : ' A pipákkal szűkítheted, mit számoljon a térkép — a becslés az országos cél-arányt vetíti a célországokra.')
  }

  document.querySelectorAll<HTMLButtonElement>('#op-seg button').forEach((b) => {
    b.addEventListener('click', () => {
      outMeasure = b.dataset['opm'] as 'trips' | 'days' | 'spending'
      document
        .querySelectorAll<HTMLButtonElement>('#op-seg button')
        .forEach((x) => x.setAttribute('aria-pressed', String(x.dataset['opm'] === outMeasure)))
      renderOutboundPurposes()
    })
  })

  /**
   * Tourism's weight in the economy, from the Tourism Satellite Account.
   *
   * The question people ask is what tourism is worth to the budget, and the
   * honest answer starts with what does not exist: KSH publishes no tax or
   * budget figure by industry. The satellite account is the published measure
   * of the same idea — direct share of GDP, share of employment, and what
   * visitors actually spend — so that is what this shows, and the note says
   * which of the two it is.
   *
   * It is also the slowest data on the site: a year's account lands about two
   * years later, and the most recent year is usually half-filled (employment
   * and spending before the GDP lines). The card therefore follows the latest
   * year where its headline figure exists, rather than the latest year in the
   * file.
   */
  const ECONOMY = DATA.economy

  function economyYear(): number | null {
    if (!ECONOMY) return null
    const headline = ECONOMY.indicators.find((i) => i.key === 'gdp-share')
    if (!headline) return null

    // The year in view when the account has reached it; otherwise the last
    // year it has, which is what "still the most recent published" means.
    const wanted = S.gran === 'y' ? yearlyPeriods[S.yi]! : monthlyPeriods[S.mi]!.slice(0, 4)
    const at = ECONOMY.years.indexOf(wanted)
    if (at >= 0 && headline.values[at] != null) return at

    for (let i = headline.values.length - 1; i >= 0; i--) {
      if (headline.values[i] != null) return i
    }
    return null
  }

  /**
   * Whether the economy card belongs on screen. A predicate rather than a
   * DOM read, because the rail layout has to know this *before* the card has
   * been rendered — reading `.hidden` there gave the previous render's answer
   * and opened a second column for a card that was about to disappear.
   */
  const economyShown = (): boolean =>
    S.view === 'stay' && S.sel == null && !!ECONOMY && economyYear() != null

  /** Same idea for the national summary and the top-five list. */
  const nationalShown = (): boolean => S.view === 'origin' || S.sel == null

  function renderEconomy(): void {
    const card = $('card-economy')
    const year = economyYear()
    card.hidden = !economyShown()
    if (card.hidden || !ECONOMY || year == null) return

    const at = (key: string): number | null =>
      ECONOMY.indicators.find((i) => i.key === key)?.values[year] ?? null
    // A decimal is meaningful on a percentage and noise on a thousand
    // billion forints, which is also a line-break waiting to happen in a
    // 130px card.
    const fmt = (value: number | null, unit: string): string =>
      value == null
        ? 'n. a.'
        : `${unit === '%' ? nf1.format(value) : nf.format(Math.round(value))} ${unit}`

    const foreign = at('foreign-spend')
    const domestic = at('domestic-spend')

    $('ec-title').textContent = `Turizmus a gazdaságban · ${ECONOMY.years[year]}`
    $('ec-kpis').innerHTML =
      kpiCard(
        'Közvetlen GDP-hozzájárulás',
        fmt(at('gdp-share'), '%'),
        liveNote(
          at('gdp-value') == null ? 'szatellit számla' : `${nf.format(at('gdp-value')!)} mrd Ft`,
        ),
      ) +
      kpiCard(
        'Foglalkoztatás',
        fmt(at('employment-share'), '%'),
        liveNote(
          at('employment-value') == null
            ? 'a nemzetgazdasághoz képest'
            : `${nf.format(at('employment-value')!)} ezer fő`,
        ),
      ) +
      kpiCard('Külföldiek kiadásai', fmt(foreign, 'mrd Ft')) +
      kpiCard('Belföldi turisták kiadásai', fmt(domestic, 'mrd Ft')) +
      // The same donut the guest-night cards use, on the money rather than on
      // the nights: it is the same question asked of a different measure.
      ratioDonut(domestic, foreign, 'nights', (value) => `${nf.format(Math.round(value))} mrd`)

    $('ec-note').textContent =
      `KSH Turizmus Szatellit Számlák (tur0031) · ${ECONOMY.years[year]}. évi adat, ` +
      `a sorozat 2004-től. Ez a turizmus GDP- és foglalkoztatási súlya — a költségvetésben ` +
      `(adóbevételben) betöltött arányáról a KSH nem közöl adatot ágazatonként.`
  }

  // ---- second rail ---------------------------------------------------------
  //
  // On a wide screen there is room for two columns of cards, and the panel
  // has outgrown one: a county card, the national summary and the top-five
  // list stack to well past a laptop's height, and a focused settlement adds
  // its own figures plus the látnivalók on top of that. So the thing the user
  // just drilled into moves one column left, and the rest of the rail stays
  // where it was — no scrolling to compare a place against the country.
  //
  // The cards are moved, not duplicated: the same DOM node, so every listener
  // (the disclosure toggle, the close button) survives the move.

  /** Below this the two columns would eat the map, so everything stacks. */
  const WIDE_RAIL = matchMedia('(min-width: 1420px)')

  function placeAuxCards(): void {
    const aux = $('panel-aux')
    const auxCard = $('aux-card')
    const panel = $('panel')
    const selCard = $('card-sel')
    const places = $('sel-places')
    const national = $('card-national')
    const top = $('card-top')
    const spend = $('card-spend')
    const purpose = $('card-outpurpose')
    const focusedSettlement = S.sel?.type === 'county' && S.focusSettlement != null

    // What moves left, in priority order: a settlement's látnivalók (its
    // figures stay on the right, where they were), otherwise the selection
    // card. In the origin view the national summary and the top-five list go
    // left as well, which puts the spending card beside them rather than a
    // screen-height below.
    const wide = WIDE_RAIL.matches
    const placesLeft = wide && focusedSettlement && !places.hidden
    // A second column is only worth opening when something stays in the
    // first one: with the national cards hidden (a selection in the map view)
    // the selection card would otherwise sit mid-screen beside an empty rail.
    const rightHasContent = nationalShown() || economyShown() || !spend.hidden || !purpose.hidden
    const cardLeft = wide && !focusedSettlement && S.sel != null && rightHasContent
    // Only worth two columns while the right one has something in it: with
    // the spending card hidden (the outbound direction) the cards would
    // otherwise hug the middle of the screen with an empty rail beside them.
    const nationalLeft =
      wide && S.view === 'origin' && nationalShown() && !(spend.hidden && purpose.hidden)

    // In the map view with nothing selected there are three national cards
    // and no reason to stack them: the top-five list moves left on its own,
    // leaving the summary and the economy card on the right.
    const topLeft = wide && !nationalLeft && S.view === 'stay' && S.sel == null && nationalShown()

    if (placesLeft) {
      if (places.parentElement !== auxCard) auxCard.append(places)
      // In a box of its own the disclosure has nothing left to hide behind,
      // so it opens with the box; the toggle still collapses it.
      if (!PL.open) openPlaces()
    } else if (places.parentElement !== selCard) {
      selCard.insertBefore(places, $('sel-spark'))
    }
    auxCard.hidden = !placesLeft

    // Both columns are rebuilt from the flags above, in their canonical
    // order, every time. Membership is decided by what *should* be left, not
    // by what happens to be there: reading the current parent instead left a
    // card stranded in the aux rail when its reason for being there went away
    // (the national cards stayed left after switching back to the map view).
    const left = [
      ...(cardLeft ? [selCard] : []),
      ...(nationalLeft ? [national, top] : []),
      ...(topLeft ? [top] : []),
      ...(placesLeft ? [auxCard] : []),
    ]
    for (const el of left) aux.append(el)
    for (const el of [selCard, national, top, $('card-economy'), spend, purpose]) {
      if (!left.includes(el)) panel.append(el)
    }
    if (!left.includes(auxCard)) aux.append(auxCard)

    aux.hidden = left.length === 0
  }

  WIDE_RAIL.addEventListener('change', () => {
    placeAuxCards()
  })

  /**
   * The national context cards. A focused settlement replaces them rather
   * than adding to them: its own card is long (figures, trend, capacity,
   * látnivalók), and "Országos összkép" is not what someone who drilled all
   * the way down to one town is reading for.
   */
  function setNationalCards(visible: boolean): void {
    $('card-national').hidden = !visible
    $('card-top').hidden = !visible
  }

  function renderSel(): void {
    const card = $('card-sel')
    if (!S.sel) {
      card.style.display = 'none'
      setNationalCards(true)
      renderPlaces(null)
      placeAuxCards()
      syncGrab()
      return
    }
    card.style.display = 'block'
    const i = idx()
    const pi = prevIdx()
    let name: string
    const vals: string[] = []
    let series: (number | null)[]
    const focused =
      S.sel.type === 'county' && S.focusSettlement != null
        ? DATA.settlements.find((s) => s.slug === S.focusSettlement)
        : undefined

    if (focused) {
      // A focused settlement pin: its own real numbers (population,
      // accommodations) — never a fabricated visitor figure of its own.
      // The sparkline below still plots the county's real yearly series,
      // labelled as such, for context.
      name = focused.name
      const acc = focused.accommodations
      const per1000 = focused.population ? (acc.total / focused.population) * 1000 : null
      vals.push(
        kpiCard(
          'Lakosság',
          `${nf.format(focused.population)} fő`,
          liveNote('KSH Helységnévtár, 2025'),
        ),
      )
      vals.push(
        kpiCard(
          'Szálláshely összesen',
          `${nf.format(acc.total)} db`,
          liveNote('élő nyilvántartás'),
        ),
      )
      vals.push(kpiCard('Nem magán szálláshely', `${nf.format(acc.nonPrivate)} db`))
      vals.push(kpiCard('Magánszálláshely', `${nf.format(acc.private)} db`))
      vals.push(kpiCard('Szálláshely / 1000 fő', per1000 == null ? 'n. a.' : nf1.format(per1000)))
      const siblings = DATA.settlements.filter((s) => s.countySlug === focused.countySlug)
      let rk = 1
      for (const s of siblings) if (s.accommodations.total > acc.total) rk++
      vals.push(kpiCard('Helyezés a megyében (szálláshely)', `${rk}. / ${siblings.length}`))
      const ci = cIdx.get(focused.countySlug)!
      series = yearlyPeriods.map((_, yi) => cVal(ci, 'y', yi, S.metric, S.scope))
    } else if (S.sel.type === 'county') {
      const ci = cIdx.get(S.sel.id)!
      name = C[ci]!.name
      const n = cVal(ci, S.gran, i, 'nights', S.scope)
      const g = cVal(ci, S.gran, i, 'guests', S.scope)
      const st = cVal(ci, S.gran, i, 'stay', S.scope)
      const domNights = cVal(ci, S.gran, i, 'nights', 'dom')
      const forNights = cVal(ci, S.gran, i, 'nights', 'for')
      vals.push(
        kpiCard(
          'Vendégéjszakák',
          fmtShort(n, 'nights'),
          yoyBadge(n, pi == null ? null : cVal(ci, S.gran, pi, 'nights', S.scope)),
        ),
      )
      vals.push(
        kpiCard(
          'Vendégek',
          fmtShort(g, 'guests'),
          yoyBadge(g, pi == null ? null : cVal(ci, S.gran, pi, 'guests', S.scope)),
        ),
      )
      vals.push(kpiCard('Átl. tartózkodás', fmtShort(st, 'stay')))
      const share = n != null ? natVal(S.gran, i, 'nights', S.scope) : null
      vals.push(
        kpiCard(
          'Országos részesedés',
          n != null && share ? `${nf1.format((n / share) * 100)} %` : 'n. a.',
        ),
      )
      const fN = cVal(ci, S.gran, i, 'nights', 'for')
      const tN = cVal(ci, S.gran, i, 'nights', 'total')
      vals.push(
        kpiCard('Külföldi arány', tN && fN != null ? `${nf1.format((fN / tN) * 100)} %` : 'n. a.'),
      )
      const my = cVal(ci, S.gran, i, S.metric, S.scope) ?? 0
      let rk = 1
      for (let cj = 0; cj < NC; cj++) if ((cVal(cj, S.gran, i, S.metric, S.scope) ?? 0) > my) rk++
      vals.push(kpiCard('Helyezés', `${rk}. / ${NC} megye`))
      const acc = accommodationsFor(S.sel.id)
      vals.push(
        kpiCard(
          'Nem magán szálláshely',
          `${nf.format(acc.nonPrivate)} db`,
          liveNote('élő nyilvántartás'),
        ),
      )
      vals.push(
        kpiCard(
          'Magánszálláshely',
          `${nf.format(acc.private)} db`,
          liveNote('szallashelyminosites.hu'),
        ),
      )
      // Who fills the beds here, as a shape — the same donut the national
      // card carries, so a county reads against the country at a glance.
      vals.push(ratioDonut(domNights, forNights, 'nights'))
      series = yearlyPeriods.map((_, yi) => cVal(ci, 'y', yi, S.metric, S.scope))
    } else {
      const ki = kIdx.get(S.sel.id)!
      name = K[ki]!.name
      const v = kVal(ki, S.gran, i)
      vals.push(
        kpiCard(
          S.flow === 'out' ? 'Magyar utazások' : 'Vendégéjszakák',
          S.flow === 'out' ? fmtFlow(v) : fmtShort(v, 'nights'),
          yoyBadge(v, pi == null ? null : kVal(ki, S.gran, pi)),
        ),
      )
      let tot = 0
      for (let k2 = 0; k2 < NK; k2++) tot += kVal(k2, S.gran, i) ?? 0
      vals.push(
        kpiCard('Részesedés', v != null && tot ? `${nf1.format((v / tot) * 100)} %` : 'n. a.'),
      )
      // The money side, from the border survey rather than the accommodation
      // register: trips, not nights, and a quarter, not a month. Every card
      // says which, because the two counts are not comparable.
      const spend = countrySpending(K[ki]!.iso)
      if (spend) {
        vals.push(
          kpiCard(
            'Költés nálunk',
            spend.spending == null ? 'n. a.' : `${nf.format(Math.round(spend.spending))} M Ft`,
            liveNote(spend.label),
          ),
        )
        vals.push(
          kpiCard(
            'Egy főre, naponta',
            spend.perDay == null ? 'n. a.' : `${nf1.format(spend.perDay)} ezer Ft`,
            liveNote('határmegfigyelés'),
          ),
        )
        vals.push(
          kpiCard(
            'Átlagos tartózkodás',
            spend.avgStay == null ? 'n. a.' : `${nf1.format(spend.avgStay)} nap`,
          ),
        )
        vals.push(
          kpiCard(
            'Utazások',
            spend.trips == null ? 'n. a.' : `${nf.format(Math.round(spend.trips))} ezer`,
            liveNote('utazás, nem vendégéjszaka'),
          ),
        )
      }
      series = yearlyPeriods.map((_, yi) => kVal(ki, 'y', yi))
    }

    $('sel-name').textContent = name
    $('sel-kpis').innerHTML = vals.join('')
    paintSpark(series)
    $('spark-cap').textContent = focused
      ? `${C[cIdx.get(focused.countySlug)!]!.name} éves idősora ${yearlyPeriods[0]}–${yearlyPeriods.at(-1)} · ${METRIC_LABEL[S.metric]} · megyei adat`
      : `Éves idősor ${yearlyPeriods[0]}–${yearlyPeriods.at(-1)} · ${
          S.sel.type === 'county'
            ? METRIC_LABEL[S.metric]
            : S.flow === 'out'
              ? 'magyar utazás (ezer)'
              : 'vendégéjszaka'
        }`
    renderAnalysis(
      S.sel.type === 'county' ? (focused?.countySlug ?? S.sel.id) : null,
      focused?.name ?? null,
    )
    renderPlaces(focused?.slug ?? null)
    // In the map view a selection replaces the national context entirely: the
    // county's own card is what the click asked for, and "Országos összkép"
    // plus the top-five list only push it down the rail. The origin view
    // keeps them, where they sit beside the country card rather than above it.
    setNationalCards(nationalShown())
    placeAuxCards()
    syncGrab()
  }

  // ---- utazási felmérés (negyedéves) ---------------------------------------
  //
  // A different KSH instrument from everything else on the map: a border
  // survey that counts *trips* — day-trippers included — their purpose, their
  // length and the money spent here. Accommodation nights and these trips are
  // never added together; they measure different things about different
  // people.
  //
  // It is published quarterly, while the timeline runs in months and years,
  // so a month is read as its own quarter and a year as the sum of its four.
  // The current month is often ahead of the latest published quarter (the
  // survey lags), and rather than showing an empty card the panel falls back
  // to the last quarter that exists and says which one it is.

  const TRAVEL = DATA.travel

  interface TravelWindow {
    /** Indices into TRAVEL.quarters — one for a month, up to four for a year. */
    indices: number[]
    label: string
    /** True when the period asked for is not published yet. */
    fallback: boolean
  }

  const quarterLabel = (key: string): string =>
    `${key.slice(0, 4)}. ${['I.', 'II.', 'III.', 'IV.'][Number(key.slice(-1)) - 1] ?? ''} negyedév`

  /**
   * `probe` says whether a given quarter carries data for the series being
   * shown. Tables in this file are published on slightly different schedules,
   * so "the latest quarter" is not one date for all of them, and the fallback
   * has to be asked per table rather than taken from the file as a whole.
   */
  function travelWindow(probe?: (index: number) => boolean): TravelWindow | null {
    if (!TRAVEL || TRAVEL.quarters.length === 0) return null
    const index = new Map(TRAVEL.quarters.map((q, i) => [q, i]))
    const has = (i: number): boolean => (probe ? probe(i) : true)

    if (S.gran === 'm') {
      const period = monthlyPeriods[S.mi]!
      const key = `${period.slice(0, 4)}-Q${Math.floor((Number(period.slice(5, 7)) - 1) / 3) + 1}`
      const at = index.get(key)
      if (at !== undefined && has(at)) {
        return { indices: [at], label: quarterLabel(key), fallback: false }
      }
    } else {
      const year = yearlyPeriods[S.yi]!
      const indices = TRAVEL.quarters
        .map((q, i) => (q.startsWith(year) && has(i) ? i : -1))
        .filter((i) => i >= 0)
      if (indices.length > 0) {
        return {
          indices,
          label: `${year}. év (${indices.length} negyedév)`,
          fallback: false,
        }
      }
    }

    let last = TRAVEL.quarters.length - 1
    while (last > 0 && !has(last)) last -= 1
    return { indices: [last], label: quarterLabel(TRAVEL.quarters[last]!), fallback: true }
  }

  /** Sum a quarterly series over the window; null when nothing is reported. */
  function travelSum(series: (number | null)[], indices: number[]): number | null {
    let total: number | null = null
    for (const i of indices) {
      const value = series[i]
      if (value == null) continue
      total = (total ?? 0) + value
    }
    return total
  }

  /**
   * Ratios are recomputed from the sums rather than averaged: KSH publishes
   * an average stay and a per-day spend per quarter, and the mean of four
   * quarterly averages is not the year's average unless the quarters are the
   * same size, which they never are. days/trips and spending/days are.
   *
   * The units work out on their own: million HUF over thousand days is
   * thousand HUF per day, which is exactly what KSH publishes.
   */
  const ratio = (top: number | null, bottom: number | null): number | null =>
    top == null || bottom == null || bottom === 0 ? null : top / bottom

  interface CountrySpend {
    label: string
    /** True when KSH publishes this country together with another one. */
    shared: boolean
    trips: number | null
    days: number | null
    spending: number | null
    avgStay: number | null
    perDay: number | null
  }

  /**
   * What one sending country spends here. Some countries are published in
   * pairs (Belgium–Luxembourg, Spain–Portugal, Serbia–Montenegro): the figure
   * is then the pair's, and every label carrying it says so rather than
   * pretending the number belongs to the one country the user clicked.
   */
  function countrySpending(iso: IsoCode): CountrySpend | null {
    const window = travelWindow()
    if (!TRAVEL || !window) return null
    const entry = TRAVEL.countries.find((c) => c.isos.includes(iso))
    if (!entry) return null

    const trips = travelSum(entry.trips, window.indices)
    const days = travelSum(entry.days, window.indices)
    const spending = travelSum(entry.spending, window.indices)
    return {
      label:
        (entry.isos.length > 1 ? `${entry.label} együtt · ` : '') +
        window.label +
        (window.fallback ? ' (legutóbbi)' : ''),
      shared: entry.isos.length > 1,
      trips,
      days,
      spending,
      avgStay: ratio(days, trips),
      perDay: ratio(spending, days),
    }
  }

  // ---- látnivalók (Wikipédia) ---------------------------------------------
  //
  // The only part of the app whose data is prose rather than a measurement,
  // and the only one fetched after first paint: places.json is ~150 kB and
  // nothing on screen needs it until somebody asks "why would I go there?".
  // Kept behind a disclosure for the same reason — it is context, not a
  // finding, and it must not push the real figures below the fold.

  /** Which settlement the panel is showing, and whether the user opened it. */
  const PL: { slug: string | null; open: boolean; rendered: string | null } = {
    slug: null,
    open: false,
    rendered: null,
  }

  const escapeHtml = (text: string): string =>
    text.replace(
      /[&<>"]/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c,
    )

  /**
   * A hotlinked Wikimedia image, or nothing. The credit line is not
   * decoration: CC BY-SA and most Commons licences require the author and
   * the licence to travel with the picture, so an image whose credit we
   * could not read never made it into the data in the first place
   * (scripts/places.ts), and what did gets it rendered here.
   */
  function placeImageHtml(image: PlaceImage | null, kind: 'hero' | 'thumb'): string {
    if (!image) return kind === 'thumb' ? '<span class="pl-thumb pl-thumb-none"></span>' : ''
    const credit = [image.artist, image.license].filter(Boolean).join(' · ')

    if (kind === 'thumb') {
      return (
        `<img class="pl-thumb" src="${image.src}" alt="" loading="lazy" decoding="async"` +
        `${credit ? ` title="${escapeHtml(credit)}"` : ''}>`
      )
    }
    return (
      `<figure class="pl-hero">` +
      `<img src="${image.src}" alt="" loading="lazy" decoding="async">` +
      (credit
        ? `<figcaption>${
            image.fileUrl
              ? `<a href="${image.fileUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(credit)}</a>`
              : escapeHtml(credit)
          }</figcaption>`
        : '') +
      `</figure>`
    )
  }

  /** Wikipedia text is other people's writing: quoted, linked, never restyled
   *  into our own voice, and always with the CC BY-SA credit attached. */
  function placesHtml(place: PlaceInfo | undefined, name: string): string {
    if (!place) {
      return `<p class="pl-empty">Ehhez a településhez nincs Wikipédia-leírás a letöltött adatban.</p>`
    }

    let html = placeImageHtml(place.image, 'hero')
    if (place.extract) {
      html += `<p class="pl-extract">${escapeHtml(place.extract)}</p>`
    }
    if (place.highlights.length > 0) {
      html +=
        `<ul class="pl-list">` +
        place.highlights
          .map(
            (h) =>
              `<li>` +
              placeImageHtml(h.image, 'thumb') +
              `<a href="${h.url}" target="_blank" rel="noopener noreferrer">${escapeHtml(h.title)}</a>` +
              `<span class="pl-km">${nf1.format(h.km)} km</span>` +
              (h.description ? `<span class="pl-desc">${escapeHtml(h.description)}</span>` : '') +
              `</li>`,
          )
          .join('') +
        `</ul>`
    } else {
      html += `<p class="pl-empty">A ${escapeHtml(name)} 7 km-es körzetében nincs önálló szócikkel bíró látnivaló.</p>`
    }

    const source = place.url
      ? `<a href="${place.url}" target="_blank" rel="noopener noreferrer">${escapeHtml(place.title ?? name)}</a> · `
      : ''
    html += `<p class="pl-src">Forrás: ${source}Wikipédia (CC BY-SA 4.0) — a szöveg és a képek a szócikkekből származnak, a képek szerzője a képre húzva olvasható.</p>`
    return html
  }

  /**
   * The images are hotlinked, so a viewer offline (or behind something that
   * blocks upload.wikimedia.org) would otherwise get broken-image icons all
   * down the panel. A failed load simply removes the picture and leaves the
   * text, which is the part that carries the meaning.
   */
  function dropBrokenImages(root: HTMLElement): void {
    root.querySelectorAll('img').forEach((img) => {
      img.addEventListener(
        'error',
        () => {
          const figure = img.closest('figure')
          if (figure) figure.remove()
          else
            img.replaceWith(
              Object.assign(document.createElement('span'), {
                className: 'pl-thumb pl-thumb-none',
              }),
            )
        },
        { once: true },
      )
    })
  }

  async function fillPlaces(slug: string, name: string): Promise<void> {
    const body = $('pl-body')
    body.innerHTML = `<p class="pl-empty">Betöltés…</p>`
    try {
      const places = await loadPlaces()
      // The user may have clicked on to another settlement while this was in
      // flight; that render owns the panel now.
      if (PL.slug !== slug) return
      body.innerHTML = placesHtml(places[slug], name)
      dropBrokenImages(body)
      PL.rendered = slug
      const count = places[slug]?.highlights.length ?? 0
      $('pl-count').textContent = count > 0 ? `${count} látnivaló` : ''
    } catch (error) {
      if (PL.slug !== slug) return
      // Three different failures used to share one message, which made the
      // real cause a guess: a 404 means the file was never deployed (or the
      // pipeline has not run), while a network error usually just means the
      // connection dropped — and that one is worth retrying.
      const message = error instanceof Error ? error.message : ''
      const missing = /HTTP 404/.test(message)
      body.innerHTML =
        `<p class="pl-empty">${
          missing
            ? 'A látnivalók adatfájlja (places.json) nincs kiszolgálva — `pnpm data:places`, majd újra kell deployolni.'
            : 'A látnivalók most nem tölthetők be (nincs kapcsolat, vagy a letöltés megszakadt).'
        }</p>` + (missing ? '' : `<button class="pl-retry" id="pl-retry">Újra</button>`)
      body.querySelector('#pl-retry')?.addEventListener('click', () => {
        // A retry has to get past the memoised loader's rejected promise.
        resetPlacesCache()
        void fillPlaces(slug, name)
      })
    }
  }

  function renderPlaces(slug: string | null): void {
    const wrap = $('sel-places')
    PL.slug = slug
    wrap.hidden = slug == null
    if (slug == null) return

    const settlement = DATA.settlements.find((s) => s.slug === slug)
    $('pl-lab').textContent = `Miért érdekes ${settlement?.name ?? ''}?`
    // Emptied on every settlement change: the count belongs to the one whose
    // data is actually loaded, and it fills in when that arrives.
    $('pl-count').textContent = ''

    const body = $('pl-body')
    body.hidden = !PL.open
    $('pl-toggle').setAttribute('aria-expanded', String(PL.open))
    // Opened once, it stays open as the user moves from pin to pin — but the
    // content has to follow the new settlement.
    if (PL.open && PL.rendered !== slug) void fillPlaces(slug, settlement?.name ?? '')
  }

  function setPlacesOpen(open: boolean): void {
    PL.open = open
    $('pl-body').hidden = !open
    $('pl-toggle').setAttribute('aria-expanded', String(open))
    if (open && PL.slug && PL.rendered !== PL.slug) {
      const settlement = DATA.settlements.find((s) => s.slug === PL.slug)
      void fillPlaces(PL.slug, settlement?.name ?? '')
    }
  }

  function openPlaces(): void {
    setPlacesOpen(true)
  }

  $('pl-toggle').addEventListener('click', () => {
    setPlacesOpen(!PL.open)
  })

  /**
   * Demand-vs-supply block for a county: how fast it grew, whether the beds
   * kept up, and how full those beds actually run at peak.
   *
   * County-level only. A focused settlement shows its county's figures with
   * that said plainly — KSH publishes neither nights nor capacity below
   * county level, so anything finer would be invented.
   */
  function renderAnalysis(countySlug: string | null, settlementName: string | null): void {
    const box = $('sel-analysis')
    const ind = countySlug ? indicatorFor(countySlug) : undefined
    // Needs the supply side; without capacity.json this block has nothing to
    // add over the KPIs above it.
    if (!ind || ind.capacity == null) {
      box.hidden = true
      return
    }
    box.hidden = false

    $('an-title').textContent = `Fejlődés és kapacitás · ${INDICATORS.from}→${INDICATORS.to}`
    const badge = $('an-signal')
    badge.textContent = ind.signal ? SIGNAL_LABEL[ind.signal] : ''
    badge.className = `an-badge${ind.signal ? ` sig-${ind.signal}` : ''}`

    const pct = (v: number | null, digits = 1): string =>
      v == null ? 'n. a.' : `${v > 0 ? '+' : ''}${nf1.format(Number(v.toFixed(digits)))} %`
    const pp = (v: number | null): string =>
      v == null ? 'n. a.' : `${v > 0 ? '+' : ''}${nf1.format(Number(v.toFixed(2)))} pp`
    const dir = (v: number | null): string =>
      v == null ? '' : v > 0 ? ' up' : v < 0 ? ' down' : ''

    const row = (label: string, value: string, cls = '', title = ''): string =>
      `<div class="an-row"${title ? ` title="${title}"` : ''}><span class="an-lab">${label}</span><span class="an-val${cls}">${value}</span></div>`

    $('an-rows').innerHTML = [
      row('Vendégéjszaka-változás', pct(ind.growth), dir(ind.growth)),
      row(
        'Férőhely-változás',
        pct(ind.capacityGrowth),
        dir(ind.capacityGrowth),
        'KSH férőhelyszám, július 31-i állapot',
      ),
      row(
        'Kereslet − kínálat',
        pp(ind.gap),
        dir(ind.gap),
        'Mennyivel nőtt gyorsabban a forgalom, mint a férőhelyszám',
      ),
      row(
        'Főszezoni kihasználtság',
        ind.occupancyPeak == null ? 'n. a.' : `${nf1.format(ind.occupancyPeak)} %`,
        '',
        `Július–augusztus. Országos medián: ${INDICATORS.medianOccupancyPeak == null ? 'n. a.' : nf1.format(INDICATORS.medianOccupancyPeak) + ' %'}`,
      ),
      row(
        'Elő- és utószezon',
        pct(ind.shoulderGrowth),
        dir(ind.shoulderGrowth),
        'Április, május, szeptember, október — a szezon hosszabbodását mutatja',
      ),
      row('Országos részesedés', pp(ind.shareShift), dir(ind.shareShift)),
    ].join('')

    $('an-note').textContent = settlementName
      ? `Megyei adat — ${settlementName} önálló forgalmi és férőhelyadata nincs közölve.`
      : 'A kihasználtság férőhelyre vetül (nem szobára), ezért teltház mellett is jóval 100 % alatt marad.'
  }

  function paintSpark(series: (number | null)[]): void {
    const svg = $('spark')
    svg.innerHTML = ''
    const W = svg.clientWidth || 280
    const H = 52
    const mx = max(series.filter((v): v is number => v != null)) ?? 1
    const bw = W / series.length
    const curYi = S.gran === 'm' ? yearlyPeriods.indexOf(monthlyPeriods[S.mi]!.slice(0, 4)) : S.yi
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`)
    series.forEach((v, j) => {
      const h = v == null ? 0 : (v / mx) * (H - 6)
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      rect.setAttribute('x', String(j * bw + 1))
      rect.setAttribute('width', String(Math.max(2, bw - 2)))
      rect.setAttribute('y', String(H - h))
      rect.setAttribute('height', String(h))
      rect.setAttribute('rx', '2')
      if (j === curYi) rect.setAttribute('class', 'cur')
      svg.append(rect)
    })
  }

  function select(sel: Selection | null): void {
    S.focusSettlement = null
    S.sel = S.sel && sel && S.sel.id === sel.id ? null : sel
    renderSel()
    // The economy card is the country-level view of the same story, so it
    // gives way to a selection and comes back when the selection clears.
    renderEconomy()
    setTargets()
    if (S.view === 'stay') flyTo(S.sel?.type === 'county' ? countyView(S.sel.id) : curView())
  }
  $('sel-close').addEventListener('click', () => select(null))
  $('legend-toggle').addEventListener('click', () => $('legendcard').classList.toggle('collapsed'))

  // ---- data drawer (tablet / phone) ---------------------------------------
  //
  // Below 1060px the panel is parked off the right edge so the cards stop
  // covering the map. This pulls it back in: swipe left from the handle (or
  // tap it) to open, swipe right anywhere on the panel to close. On desktop
  // the CSS keeps the panel a permanent rail and hides the handle, so all of
  // this is inert there.
  const grabEl = $<HTMLButtonElement>('panel-grab')
  /** True while the off-canvas drawer is mid-slide; see tick(). */
  function drawerSliding(): boolean {
    return performance.now() < drawerUntil
  }

  /** Kept in step with --drawer-dur in app.css. */
  const DRAWER_MS =
    Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--drawer-dur'),
    ) || 280
  /**
   * The slide is a transform, which should be cheap — but the cards inside
   * the drawer are glass (backdrop-filter), and re-blurring what is behind a
   * moving element costs a full repaint every frame. Next to a three.js scene
   * that is already asking for the GPU, that dropped the close to two or
   * three frames: the drawer appeared to vanish rather than to slide.
   *
   * So the blur is swapped for a solid fill for exactly as long as the panel
   * is moving — the same colour at full opacity, which is imperceptible at
   * 280ms — and the layer is promoted up front so the compositor has it ready.
   */
  let drawerAnim: ReturnType<typeof setTimeout> | undefined
  let drawerUntil = 0
  function setDrawer(open: boolean): void {
    document.body.classList.add('drawering')
    clearTimeout(drawerAnim)
    drawerAnim = setTimeout(() => document.body.classList.remove('drawering'), DRAWER_MS)
    drawerUntil = performance.now() + DRAWER_MS

    document.body.classList.toggle('panel-open', open)
    grabEl.setAttribute('aria-expanded', String(open))
    grabEl.setAttribute('aria-label', open ? 'Adatok bezárása' : 'Adatok megnyitása')
  }
  /** Name the closed handle after the current selection, so a pick the user
   *  made on the map is not silently hidden behind it. */
  function syncGrab(): void {
    const name = S.sel ? $('sel-name').textContent : null
    grabEl.classList.toggle('has-sel', !!name)
    $('panel-grab-label').textContent = name || 'Adatok'
  }

  let swipe: { x: number; y: number; id: number } | null = null
  let swiped = false
  const SWIPE_MIN = 45
  function swipeStart(e: PointerEvent): void {
    swipe = { x: e.clientX, y: e.clientY, id: e.pointerId }
    swiped = false
  }
  function swipeEnd(e: PointerEvent): void {
    if (!swipe || swipe.id !== e.pointerId) return
    const dx = e.clientX - swipe.x
    const dy = e.clientY - swipe.y
    swipe = null
    // Horizontal intent only: the panel scrolls vertically, and a drag down
    // its list must not be read as a close.
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return
    swiped = true
    setDrawer(dx < 0)
  }
  for (const el of [grabEl, $('panel')]) el.addEventListener('pointerdown', swipeStart)
  // The gesture ends wherever the finger happens to be — off the handle and
  // over the canvas, most of the time — so the end is watched on the window
  // rather than on the element that started it. Pointer capture would also
  // solve that, but capturing on the panel swallowed clicks on the controls
  // inside it (the selection's close button stopped working).
  addEventListener('pointerup', swipeEnd)
  addEventListener('pointercancel', () => {
    swipe = null
  })
  grabEl.addEventListener('click', () => {
    // A recognised swipe already decided the state; the click that follows it
    // would immediately undo that.
    if (swiped) {
      swiped = false
      return
    }
    setDrawer(!document.body.classList.contains('panel-open'))
  })

  // ---- tooltip --------------------------------------------------------------

  const tipEl = $('tooltip')
  function showTip(x: number, y: number, html: string): void {
    tipEl.innerHTML = html
    tipEl.style.opacity = '1'
    const w = tipEl.offsetWidth,
      h = tipEl.offsetHeight
    let px = x + 14,
      py = y - h - 10
    if (px + w > innerWidth - 8) px = x - w - 14
    if (px < 8) px = 8
    if (py < 8) py = y + 16
    // Tall cards (the trend sparkline made them taller) can now run off the
    // bottom when the anchor is a row near the end of the kimutatás table.
    if (py + h > innerHeight - 8) py = Math.max(8, innerHeight - h - 8)
    tipEl.style.left = `${px}px`
    tipEl.style.top = `${py}px`
  }
  function hideTip(): void {
    tipEl.style.opacity = '0'
  }

  // The three tooltips share one small vocabulary — an eyebrow for context,
  // label/value rows that align on the right, and a single source footnote —
  // so a hover on a county, a country and a settlement all read the same way
  // instead of each being its own paragraph of prose.
  const tipHead = (title: string, eyebrow?: string): string =>
    `<div class="tt-head"><span class="t">${title}</span>` +
    (eyebrow ? `<span class="tt-eyebrow">${eyebrow}</span>` : '') +
    `</div>`
  const tipRow = (label: string, value: string): string =>
    `<div class="tt-row"><span>${label}</span><b>${value}</b></div>`

  /**
   * The trend, as a shape rather than a sentence: the whole published series
   * for the current granularity, with the period being viewed marked on it.
   * Deliberately unlabelled on the axes — it is there to answer "is this
   * place rising or sinking, and how bumpy is the ride", which a number in a
   * row cannot show, and the exact values live in the rows above it.
   */
  function tipSpark(series: (number | null)[], hi: number, caption: string): string {
    const pts = series
      .map((v, i) => ({ i, v }))
      .filter((p): p is { i: number; v: number } => p.v != null)
    if (pts.length < 3) return ''

    const W = 226
    const H = 44
    const PAD = 4
    let lo = Infinity
    let hiV = -Infinity
    for (const p of pts) {
      if (p.v < lo) lo = p.v
      if (p.v > hiV) hiV = p.v
    }
    // A flat series would divide by zero; drawing it along the middle is
    // both safe and honest (nothing is happening).
    const span = hiV - lo || 1
    const x = (i: number): number => PAD + (i / Math.max(1, series.length - 1)) * (W - 2 * PAD)
    const y = (v: number): number => H - PAD - ((v - lo) / span) * (H - 2 * PAD)

    const line = pts.map((p, k) => `${k ? 'L' : 'M'}${x(p.i).toFixed(1)} ${y(p.v).toFixed(1)}`)
    const first = pts[0]!
    const last = pts.at(-1)!
    const area = `${line.join(' ')} L${x(last.i).toFixed(1)} ${H} L${x(first.i).toFixed(1)} ${H} Z`
    const cur = pts.find((p) => p.i === hi) ?? last

    return (
      `<svg class="tt-spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true">` +
      `<path class="a" d="${area}"/><path class="l" d="${line.join(' ')}"/>` +
      `<circle class="d" cx="${x(cur.i).toFixed(1)}" cy="${y(cur.v).toFixed(1)}" r="3"/>` +
      `</svg><div class="tt-cap">${caption}</div>`
    )
  }

  /** Change against the same period a year earlier, as a coloured row. */
  function tipYoY(now: number | null, before: number | null): string {
    if (now == null || before == null || before === 0) return ''
    const pct = ((now - before) / before) * 100
    const cls = pct > 0.05 ? 'up' : pct < -0.05 ? 'down' : ''
    const label = S.gran === 'm' ? 'egy évvel korábbihoz' : 'előző évhez'
    return (
      `<div class="tt-row"><span>${label}</span>` +
      `<b class="tt-delta ${cls}">${pct > 0 ? '+' : ''}${nf1.format(Number(pct.toFixed(1)))} %</b></div>`
    )
  }

  /** The full published series for one county, at the granularity in view. */
  const countySeries = (ci: number): (number | null)[] =>
    S.gran === 'm'
      ? monthlyPeriods.map((_, mi) => cVal(ci, 'm', mi, S.metric, S.scope))
      : yearlyPeriods.map((_, yi) => cVal(ci, 'y', yi, S.metric, S.scope))

  const spanLabel = (): string =>
    S.gran === 'm'
      ? `${monthlyPeriods[0]!.replace('-', '. ')}. – ${monthlyPeriods.at(-1)!.replace('-', '. ')}.`
      : `${yearlyPeriods[0]}–${yearlyPeriods.at(-1)}`

  function tipCounty(slug: string): string {
    const ci = cIdx.get(slug)!
    const v = cVal(ci, S.gran, idx(), S.metric, S.scope)
    const nd = cVal(ci, S.gran, idx(), 'nights', 'dom')
    const nfr = cVal(ci, S.gran, idx(), 'nights', 'for')

    let rows = tipRow(METRIC_LABEL[S.metric], fmtFull(v, S.metric))
    if (S.metric === 'nights' && S.scope === 'total') {
      rows +=
        tipRow('ebből belföldi', fmtShort(nd, 'nights')) +
        tipRow('ebből külföldi', fmtShort(nfr, 'nights'))
    }
    if (nd != null && nfr != null && nd + nfr > 0) {
      rows += tipRow('külföldi arány', `${nf1.format((nfr / (nd + nfr)) * 100)} %`)
    }
    const pi = prevIdx()
    rows += tipYoY(v, pi == null ? null : cVal(ci, S.gran, pi, S.metric, S.scope))

    return (
      tipHead(C[ci]!.name, periodLabel()) +
      `<div class="tt-rows">${rows}</div>` +
      tipSpark(
        countySeries(ci),
        idx(),
        `${spanLabel()} · ${METRIC_LABEL[S.metric].toLowerCase()}`,
      ) +
      `<div class="p">${SCOPE_LABEL[S.scope]} · KSH</div>`
    )
  }

  function tipCountry(iso: string): string {
    const ki = kIdx.get(iso)!
    const v = kVal(ki, S.gran, idx())
    let tot = 0
    for (let i = 0; i < NK; i++) tot += kVal(i, S.gran, idx()) ?? 0
    const spend = countrySpending(K[ki]!.iso)
    const money = spend
      ? `<div class="tt-sep"></div>` +
        tipRow(
          'költés nálunk',
          spend.spending == null ? 'n. a.' : `${nf.format(Math.round(spend.spending))} M Ft`,
        ) +
        tipRow(
          'egy főre, naponta',
          spend.perDay == null ? 'n. a.' : `${nf1.format(spend.perDay)} ezer Ft`,
        ) +
        tipRow(
          'átlagos tartózkodás',
          spend.avgStay == null ? 'n. a.' : `${nf1.format(spend.avgStay)} nap`,
        )
      : ''

    return (
      tipHead(K[ki]!.name, periodLabel()) +
      `<div class="tt-rows">` +
      tipRow(
        S.flow === 'out' ? 'magyar utazások' : 'vendégéjszakák',
        S.flow === 'out' ? fmtFlow(v) : fmtFull(v, 'nights'),
      ) +
      tipRow(
        S.flow === 'out' ? 'a kiemelt célországokból' : 'a kiemelt országokból',
        `${nf1.format(tot > 0 && v != null ? (v / tot) * 100 : 0)} %`,
      ) +
      money +
      (purposeFiltered()
        ? `<div class="tt-cap">Becslés: az országos cél-arány a célországra vetítve — a KSH ezt nem közli országonként.</div>`
        : '') +
      (spend
        ? `<div class="tt-cap">${spend.label} · határmegfigyelés, utazásra vetítve</div>`
        : '') +
      `</div>` +
      `<div class="p">küldő ország szerint · KSH</div>`
    )
  }

  /**
   * Everything the settlement tooltip needs — a subset of BarUserData, so
   * both the map pin and a kimutatás row can ask for the same card.
   */
  type SettlementTip = Pick<BarUserData, 'name' | 'countySlug' | 'population' | 'accommodations'>

  function tipSettlement(bud: SettlementTip): string {
    const ci = cIdx.get(bud.countySlug!)!
    const county = C[ci]!
    const countyNights = cVal(ci, S.gran, idx(), 'nights', 'total')
    const pi = prevIdx()
    const acc = bud.accommodations

    // The private / non-private split is a proportion, so it gets the same
    // little bar the kimutatás table uses rather than a second pair of
    // numbers competing with the total above it.
    let accBlock = ''
    if (acc && acc.total > 0) {
      const privShare = acc.private / acc.total
      accBlock =
        tipRow('minősített szálláshely', `${nf.format(acc.total)} db`) +
        `<div class="tt-split">` +
        `<span class="sh-splitbar"><i class="p" style="width:${privShare * 100}%"></i>` +
        `<i class="b" style="width:${(1 - privShare) * 100}%"></i></span>` +
        `<span class="tt-cap"><em class="key-priv">${nf.format(acc.private)} magán</em> · ` +
        `<em class="key-biz">${nf.format(acc.nonPrivate)} nem magán</em></span>` +
        `</div>`
    }

    // The county's guest-nights figure is the one number here that is *not*
    // about this settlement, so it sits below a rule and says so — the
    // separation is the honesty, since KSH publishes no settlement-level
    // tourism volume at all.
    // Budapest is its own county, so the eyebrow would just repeat the name.
    const region = county.name.replace(/ vármegye$/, '')
    return (
      tipHead(bud.name!, region === bud.name ? undefined : region) +
      `<div class="tt-rows">` +
      tipRow('lakosság', `${nf.format(bud.population!)} fő`) +
      accBlock +
      `<div class="tt-sep"></div>` +
      tipRow('vármegyei vendégéjszakák', fmtFull(countyNights, 'nights')) +
      tipYoY(countyNights, pi == null ? null : cVal(ci, S.gran, pi, 'nights', 'total')) +
      `</div>` +
      tipSpark(
        S.gran === 'm'
          ? monthlyPeriods.map((_, mi) => cVal(ci, 'm', mi, 'nights', 'total'))
          : yearlyPeriods.map((_, yi) => cVal(ci, 'y', yi, 'nights', 'total')),
        idx(),
        `${spanLabel()} · ${county.name} vendégéjszakái — nem a település saját forgalma`,
      ) +
      `<div class="p">KSH Helységnévtár, 2025 · szallashelyminosites.hu, élő nyilvántartás</div>`
    )
  }

  // ---- kimutatás tábla --------------------------------------------------
  //
  // Real columns only. The design prototype this was ported from also had
  // "vendégéjszaka" and "10 éves növekedés" columns — both were made-up
  // county shares dressed up as per-settlement figures. Confirmed (again,
  // this time against KSH's T-STAR settlement-level database too, not just
  // STADAT) that no real settlement-level guest-nights figure exists
  // anywhere in open KSH data, so those columns are dropped rather than
  // filled with another estimate.

  /**
   * Kimutatás tábla. Two datasets behind one sortable grid:
   *
   *  - Vármegyék — the development picture: growth, share shift, whether the
   *    beds kept pace, and how full they run at peak. This is the only level
   *    where KSH publishes both nights and capacity.
   *  - Települések — population and certified-accommodation counts, which do
   *    exist per settlement. Deliberately no nights column: none is published
   *    below county level, and estimating one would invent the answer.
   */
  type ShDataset = 'counties' | 'settlements' | 'motivations' | 'ages'
  const SH: {
    open: boolean
    dataset: ShDataset
    sort: string
    dir: 1 | -1
    mode: 'accom' | 'pop'
    tripLength: TripLength
    /** Which population the motivation table is about. */
    direction: 'in' | 'out' | 'home'
    /** Which trips the age split is about. */
    destination: AgeDestination
  } = {
    open: false,
    dataset: 'counties',
    sort: 'growth',
    dir: -1,
    mode: 'accom',
    tripLength: 'multiDay',
    direction: 'in',
    destination: 'world',
  }

  /** One column: how to render it, and what to sort it by. */
  interface SheetCol<R> {
    k: string
    lab: string
    cls?: string
    sortable?: boolean
    /** Sort key. Strings collate in Hungarian; nulls always sink. */
    key?: (r: R) => string | number | null
    cell: (r: R) => string
  }

  const naCell = '<span class="sh-na">n. a.</span>'
  const signed = (v: number | null, unit: string, digits = 1): string =>
    v == null
      ? naCell
      : `<span class="sh-g ${v > 0 ? 'up' : v < 0 ? 'down' : ''}">${v > 0 ? '+' : ''}${nf1.format(
          Number(v.toFixed(digits)),
        )} ${unit}</span>`

  // ---- counties ----------------------------------------------------------

  const COUNTY_COLS: SheetCol<CountyIndicators>[] = [
    { k: 'rk', lab: '#', sortable: false, cell: () => '' },
    {
      k: 'name',
      lab: 'Vármegye',
      cls: 'l',
      key: (r) => r.name,
      cell: (r) =>
        `<div class="sh-nm">${r.name}</div><div class="sh-cty">${
          r.signal ? SIGNAL_LABEL[r.signal] : '—'
        }</div>`,
    },
    {
      k: 'nights',
      lab: 'Vendégéjszaka',
      key: (r) => r.nights,
      cell: (r) => fmtShort(r.nights, 'nights'),
    },
    { k: 'growth', lab: 'Növekedés', key: (r) => r.growth, cell: (r) => signed(r.growth, '%') },
    {
      k: 'shareShift',
      lab: 'Részesedés',
      key: (r) => r.shareShift,
      cell: (r) => signed(r.shareShift, 'pp', 2),
    },
    {
      k: 'capacity',
      lab: 'Férőhely',
      key: (r) => r.capacity,
      cell: (r) => (r.capacity == null ? naCell : nf.format(r.capacity)),
    },
    {
      k: 'capacityGrowth',
      lab: 'Férőhely-vált.',
      key: (r) => r.capacityGrowth,
      cell: (r) => signed(r.capacityGrowth, '%'),
    },
    {
      k: 'occupancyPeak',
      lab: 'Főszezoni kihaszn.',
      key: (r) => r.occupancyPeak,
      cell: (r) =>
        r.occupancyPeak == null ? naCell : `<strong>${nf1.format(r.occupancyPeak)} %</strong>`,
    },
    {
      k: 'shoulderGrowth',
      lab: 'Elő-/utószezon',
      key: (r) => r.shoulderGrowth,
      cell: (r) => signed(r.shoulderGrowth, '%'),
    },
  ]

  // ---- settlements -------------------------------------------------------

  interface SettlementRow {
    s: (typeof DATA.settlements)[number]
    countyName: string
    acc: AccommodationCounts
    privShare: number
    per1000: number | null
    val: number
  }

  function settlementRows(): SettlementRow[] {
    return DATA.settlements.map((s) => {
      const acc = s.accommodations
      return {
        s,
        countyName: C[cIdx.get(s.countySlug)!]!.name,
        acc,
        privShare: acc.total ? acc.private / acc.total : 0,
        per1000: s.population ? (acc.total / s.population) * 1000 : null,
        val: SH.mode === 'pop' ? s.population : acc.total,
      }
    })
  }

  const SETTLEMENT_COLS: SheetCol<SettlementRow>[] = [
    { k: 'rk', lab: '#', sortable: false, cell: () => '' },
    {
      k: 'name',
      lab: 'Település',
      cls: 'l',
      key: (r) => r.s.name,
      cell: (r) => `<div class="sh-nm">${r.s.name}</div><div class="sh-cty">${r.countyName}</div>`,
    },
    {
      k: 'pop',
      lab: 'Lakosság',
      key: (r) => r.s.population,
      cell: (r) => nf.format(r.s.population),
    },
    {
      k: 'accom',
      lab: 'Szálláshely',
      key: (r) => r.acc.total,
      cell: (r) => `<strong>${nf.format(r.acc.total)}</strong>`,
    },
    {
      k: 'split',
      lab: 'Magán arány',
      key: (r) => r.privShare,
      cell: (r) =>
        `<div class="sh-split" title="${nf.format(r.acc.private)} magán · ${nf.format(
          r.acc.nonPrivate,
        )} nem magán szálláshely">` +
        `<span class="sh-splitbar"><i class="p" style="width:${r.privShare * 100}%"></i><i class="b" style="width:${
          (1 - r.privShare) * 100
        }%"></i></span>` +
        `<span class="sh-pct">${nf1.format(r.privShare * 100)} %</span></div>`,
    },
    {
      k: 'per1000',
      lab: 'Szálláshely / 1000 fő',
      key: (r) => r.per1000,
      cell: (r) => (r.per1000 == null ? naCell : nf1.format(r.per1000)),
    },
  ]

  // ---- motivations -------------------------------------------------------
  //
  // Trips, not nights. This is the border survey (tur0045), so it counts
  // people crossing the border for a reason, including the ones who never
  // sleep here — which is why it is a table of its own rather than another
  // column next to the accommodation figures.

  interface MotivationRow {
    key: string
    label: string
    level: 0 | 1 | 2
    /** Thousands of trips / days, million HUF, over the current window. */
    trips: number | null
    days: number | null
    spending: number | null
    avgStay: number | null
    perDay: number | null
  }

  function motivationRows(): MotivationRow[] {
    const window = travelWindow(motivationProbe())
    if (!TRAVEL || !window) return []
    const outbound = SH.direction === 'out'
    const series = outbound
      ? TRAVEL.outbound.motivations[SH.tripLength]
      : TRAVEL.motivations[SH.tripLength]
    const purposes = outbound ? TRAVEL.outbound.purposes : TRAVEL.purposes

    return purposes.map((purpose) => {
      const entry = series[purpose.key]
      const trips = entry ? travelSum(entry.trips, window.indices) : null
      const days = entry ? travelSum(entry.days, window.indices) : null
      const spending = entry ? travelSum(entry.spending, window.indices) : null
      return {
        key: purpose.key,
        label: purpose.label,
        level: purpose.level,
        trips,
        days,
        spending,
        avgStay: ratio(days, trips),
        perDay: ratio(spending, days),
      }
    })
  }

  /**
   * The outbound purpose table is published a quarter later than the others,
   * so "the latest quarter" is not the same date for both. The window falls
   * back to the last quarter this particular table actually reports.
   */
  function motivationProbe(): ((index: number) => boolean) | undefined {
    if (!TRAVEL) return undefined
    const total =
      SH.direction === 'out'
        ? TRAVEL.outbound.motivations[SH.tripLength]['osszesen']
        : TRAVEL.motivations[SH.tripLength]['osszesen']
    return total ? (index: number) => total.trips[index] != null : undefined
  }

  /** Where residents travel inside the country, by tourism region. */
  interface DomesticRow {
    label: string
    trips: number | null
    days: number | null
    avgStay: number | null
  }

  function domesticRows(): DomesticRow[] {
    const window = travelWindow()
    if (!TRAVEL || !window) return []
    return TRAVEL.domestic.map((region) => {
      const trips = travelSum(region.trips, window.indices)
      const days = travelSum(region.days, window.indices)
      return { label: region.label, trips, days, avgStay: ratio(days, trips) }
    })
  }

  const DOMESTIC_COLS: SheetCol<DomesticRow>[] = [
    { k: 'rk', lab: '#', sortable: false, cell: () => '' },
    {
      k: 'name',
      lab: 'Célrégió',
      cls: 'l',
      key: (r) => r.label,
      cell: (r) => `<div class="sh-nm">${r.label}</div>`,
    },
    {
      k: 'trips',
      lab: 'Utazás (ezer)',
      key: (r) => r.trips,
      cell: (r) =>
        r.trips == null ? naCell : `<strong>${nf.format(Math.round(r.trips))}</strong>`,
    },
    {
      k: 'days',
      lab: 'Eltöltött idő (ezer nap)',
      key: (r) => r.days,
      cell: (r) => (r.days == null ? naCell : nf.format(Math.round(r.days))),
    },
    {
      k: 'avgStay',
      lab: 'Átlagos hossz (nap)',
      key: (r) => r.avgStay,
      cell: (r) => (r.avgStay == null ? naCell : nf1.format(r.avgStay)),
    },
  ]

  const MOTIVATION_COLS: SheetCol<MotivationRow>[] = [
    { k: 'rk', lab: '#', sortable: false, cell: () => '' },
    {
      k: 'name',
      lab: 'Utazás célja',
      cls: 'l',
      key: (r) => r.label,
      // The indentation is KSH's own: level 1 rows are parts of the level 0
      // above them, level 2 parts of that. Flattening them would invite
      // adding a total to its own components.
      cell: (r) => `<div class="sh-nm mo-l${r.level}">${r.label}</div>`,
    },
    {
      k: 'trips',
      lab: 'Utazás (ezer)',
      key: (r) => r.trips,
      cell: (r) =>
        r.trips == null ? naCell : `<strong>${nf.format(Math.round(r.trips))}</strong>`,
    },
    {
      k: 'days',
      lab: 'Eltöltött idő (ezer nap)',
      key: (r) => r.days,
      cell: (r) => (r.days == null ? naCell : nf.format(Math.round(r.days))),
    },
    {
      k: 'avgStay',
      lab: 'Átlagos hossz (nap)',
      key: (r) => r.avgStay,
      cell: (r) => (r.avgStay == null ? naCell : nf1.format(r.avgStay)),
    },
    {
      k: 'spending',
      lab: 'Költés (millió Ft)',
      key: (r) => r.spending,
      cell: (r) => (r.spending == null ? naCell : nf.format(Math.round(r.spending))),
    },
    {
      k: 'perDay',
      lab: 'Költés / fő / nap (ezer Ft)',
      key: (r) => r.perDay,
      cell: (r) => (r.perDay == null ? naCell : nf1.format(r.perDay)),
    },
  ]

  /** The same hover card as everywhere else, with the purpose's own trend. */
  function tipMotivation(row: MotivationRow): string {
    const window = travelWindow()
    const entry = TRAVEL?.motivations[SH.tripLength][row.key]
    const lengthLabel = SH.tripLength === 'oneDay' ? 'egynapos' : 'többnapos'

    return (
      tipHead(row.label, window?.label ?? '') +
      `<div class="tt-rows">` +
      tipRow('utazás', row.trips == null ? 'n. a.' : `${nf.format(Math.round(row.trips))} ezer`) +
      tipRow(
        'eltöltött idő',
        row.days == null ? 'n. a.' : `${nf.format(Math.round(row.days))} ezer nap`,
      ) +
      tipRow('átlagos hossz', row.avgStay == null ? 'n. a.' : `${nf1.format(row.avgStay)} nap`) +
      tipRow(
        'költés',
        row.spending == null ? 'n. a.' : `${nf.format(Math.round(row.spending))} millió Ft`,
      ) +
      tipRow(
        'egy főre, naponta',
        row.perDay == null ? 'n. a.' : `${nf1.format(row.perDay)} ezer Ft`,
      ) +
      `</div>` +
      (entry && TRAVEL
        ? tipSpark(
            entry.trips,
            window?.indices.at(-1) ?? TRAVEL.quarters.length - 1,
            `${quarterLabel(TRAVEL.quarters[0]!)} – ${quarterLabel(TRAVEL.quarters.at(-1)!)} · ${lengthLabel} utazások száma`,
          )
        : '') +
      `<div class="p">KSH határmegfigyelés (tur0045) · utazás, nem vendégéjszaka</div>`
    )
  }

  // ---- korcsoportok (Eurostat) ---------------------------------------------
  //
  // The one dimension KSH never publishes, and the only dataset here that is
  // not Hungarian: Eurostat surveys residents aged 15 and over about their
  // own trips. So this table is about *us* travelling, at home and abroad,
  // not about the visitors every other table counts. Annual, 2012-.

  const AGES = DATA.ages

  interface AgeRow {
    key: string
    label: string
    trips: number | null
    nights: number | null
    share: number | null
    avgStay: number | null
  }

  /** The year in view, or the last one published, as everywhere else here. */
  function ageYear(): { index: number; label: string; fallback: boolean } | null {
    if (!AGES || AGES.years.length === 0) return null
    const wanted = S.gran === 'y' ? yearlyPeriods[S.yi]! : monthlyPeriods[S.mi]!.slice(0, 4)
    const at = AGES.years.indexOf(wanted)
    if (at >= 0) return { index: at, label: `${wanted}. év`, fallback: false }
    const last = AGES.years.length - 1
    return { index: last, label: `${AGES.years[last]}. év`, fallback: true }
  }

  function ageRows(): AgeRow[] {
    const year = ageYear()
    if (!AGES || !year) return []
    const series = AGES.series[SH.destination]

    const total = AGES.groups.reduce(
      (sum, group) => sum + (series.trips[group.key]?.[year.index] ?? 0),
      0,
    )
    return AGES.groups.map((group) => {
      const trips = series.trips[group.key]?.[year.index] ?? null
      const nights = series.nights[group.key]?.[year.index] ?? null
      return {
        key: group.key,
        label: group.label,
        trips,
        nights,
        share: trips == null || total === 0 ? null : (trips / total) * 100,
        avgStay: ratio(nights, trips),
      }
    })
  }

  const AGE_COLS: SheetCol<AgeRow>[] = [
    { k: 'rk', lab: '#', sortable: false, cell: () => '' },
    {
      k: 'name',
      lab: 'Korcsoport',
      cls: 'l',
      key: (r) => r.label,
      cell: (r) => `<div class="sh-nm">${r.label}</div>`,
    },
    {
      k: 'trips',
      lab: 'Utazás',
      key: (r) => r.trips,
      cell: (r) => (r.trips == null ? naCell : `<strong>${nf.format(r.trips)}</strong>`),
    },
    {
      k: 'share',
      lab: 'Részesedés',
      key: (r) => r.share,
      cell: (r) =>
        r.share == null
          ? naCell
          : // One category against the whole, so the remainder stays the bar's
            // own background — a second colour would read as another group.
            `<div class="sh-split"><span class="sh-splitbar"><i class="p" style="width:${r.share}%"></i>` +
            `</span><span class="sh-pct">${nf1.format(r.share)} %</span></div>`,
    },
    {
      k: 'nights',
      lab: 'Vendégéjszaka',
      key: (r) => r.nights,
      cell: (r) => (r.nights == null ? naCell : nf.format(r.nights)),
    },
    {
      k: 'avgStay',
      lab: 'Átlagos hossz (nap)',
      key: (r) => r.avgStay,
      cell: (r) => (r.avgStay == null ? naCell : nf1.format(r.avgStay)),
    },
  ]

  function tipAge(row: AgeRow): string {
    const year = ageYear()
    const series = AGES?.series[SH.destination]
    return (
      tipHead(row.label, year?.label ?? '') +
      `<div class="tt-rows">` +
      tipRow('utazás', row.trips == null ? 'n. a.' : nf.format(row.trips)) +
      tipRow('részesedés', row.share == null ? 'n. a.' : `${nf1.format(row.share)} %`) +
      tipRow('vendégéjszaka', row.nights == null ? 'n. a.' : nf.format(row.nights)) +
      tipRow('átlagos hossz', row.avgStay == null ? 'n. a.' : `${nf1.format(row.avgStay)} nap`) +
      `</div>` +
      (series && AGES
        ? tipSpark(
            series.trips[row.key] ?? [],
            year?.index ?? AGES.years.length - 1,
            `${AGES.years[0]}–${AGES.years.at(-1)} · ${series.label.toLowerCase()}, utazások száma`,
          )
        : '') +
      `<div class="p">Eurostat · a magyar lakosság saját utazásai (15 éves kortól)</div>`
    )
  }

  function renderAgeSheet(): void {
    const rows = ageRows()
    const year = ageYear()
    const series = AGES?.series[SH.destination]

    renderSheetTable(
      AGE_COLS,
      rows,
      (r) => r.trips,
      () => '',
      () => false,
      () => {},
      (r) => tipAge(r),
      (r) => r.label,
      'Utazások száma',
    )

    $('sh-sub').textContent = year
      ? `${series?.label ?? ''} korcsoport szerint · ${year.label}` +
        `${year.fallback ? ' (a legutóbbi közölt év)' : ''} · Eurostat tour_dem_ttage / tour_dem_tnage — ` +
        `a magyar lakosság saját utazásai, nem a hozzánk érkező vendégek`
      : 'Nincs betöltve korcsoportos adat (pnpm data:ages).'

    const trips = rows.reduce((sum, r) => sum + (r.trips ?? 0), 0)
    const nights = rows.reduce((sum, r) => sum + (r.nights ?? 0), 0)
    const biggest = [...rows].sort((a, b) => (b.trips ?? 0) - (a.trips ?? 0))[0]
    $('sh-tot').innerHTML =
      `Összesen <strong>${nf.format(trips)}</strong> utazás · <strong>${nf.format(nights)}</strong> vendégéjszaka` +
      (biggest
        ? ` — a legtöbbet a <strong>${biggest.label}</strong> korosztály utazik ` +
          `(${biggest.share == null ? 'n. a.' : `${nf1.format(biggest.share)} %`})`
        : '') +
      `. A felmérés a 15 éves és idősebb lakosságra terjed ki.`
  }

  // ---- shared rendering --------------------------------------------------

  /** Sort in place; nulls sink either way so they never head the table. */
  function sortRows<R>(rows: R[], col: SheetCol<R> | undefined, dir: 1 | -1): void {
    if (!col?.key) return
    const key = col.key
    rows.sort((a, b) => {
      const A = key(a)
      const B = key(b)
      if (A == null && B == null) return 0
      if (A == null) return 1
      if (B == null) return -1
      return (typeof A === 'string' ? A.localeCompare(B as string, 'hu') : A - (B as number)) * dir
    })
  }

  /**
   * The distribution, drawn beside the table: one bar per row, in whatever
   * order the table is currently in, so running an eye down the rows runs it
   * along the chart. The bar for the hovered row lights up and the caption
   * names it — the chart answers "where does this one sit among the rest",
   * which no single row can.
   */
  interface SheetChartPoint {
    label: string
    value: number | null
  }

  let sheetChart: { points: SheetChartPoint[]; unit: string } = { points: [], unit: '' }

  function paintSheetChart(points: SheetChartPoint[], title: string, unit: string): void {
    sheetChart = { points, unit }
    const svg = $('sh-chart-svg')
    $('sh-chart-title').textContent = title

    const values = points.map((p) => p.value).filter((v): v is number => v != null)
    const top = Math.max(0, ...values)
    const bottom = Math.min(0, ...values)
    // Growth columns go negative, so the baseline sits wherever zero falls
    // rather than always at the bottom: a shrinking county has to read as
    // shrinking, not as a very short bar.
    const span = top - bottom || 1
    const W = 1000
    const H = 400
    const zero = (top / span) * H
    const gap = points.length > 60 ? 0.5 : points.length > 25 ? 1.5 : 3
    const step = W / Math.max(1, points.length)
    const width = Math.max(1, step - gap)

    svg.setAttribute('viewBox', `0 0 ${W} ${H}`)
    svg.innerHTML =
      (bottom < 0
        ? `<line x1="0" x2="${W}" y1="${zero.toFixed(2)}" y2="${zero.toFixed(2)}" ` +
          `stroke="currentColor" stroke-width="1" opacity="0.25"></line>`
        : '') +
      points
        .map((point, i) => {
          const value = point.value ?? 0
          const h = (Math.abs(value) / span) * H
          const y = value >= 0 ? zero - h : zero
          return (
            `<rect data-i="${i}" x="${(i * step).toFixed(2)}" y="${y.toFixed(2)}" ` +
            `width="${width.toFixed(2)}" height="${h.toFixed(2)}" rx="${width > 6 ? 2 : 0}"></rect>`
          )
        })
        .join('')
    highlightSheetChart(null)
  }

  function highlightSheetChart(index: number | null): void {
    const svg = $('sh-chart-svg')
    svg.querySelectorAll('rect').forEach((rect, i) => {
      rect.classList.toggle('hot', i === index)
    })

    const point = index == null ? null : sheetChart.points[index]
    const value =
      point?.value == null
        ? 'nincs adat'
        : Math.abs(point.value) < 100
          ? nf1.format(point.value)
          : nf.format(Math.round(point.value))
    $('sh-chart-cap').innerHTML = point
      ? `<b>${point.label}</b>${value}${sheetChart.unit ? ` ${sheetChart.unit}` : ''} · ` +
        `${index! + 1}. a táblázat sorrendjében`
      : sheetChart.points.length > 0
        ? `${sheetChart.points.length} sor · vidd az egeret egy sor fölé`
        : ''
  }

  function renderSheetTable<R>(
    cols: SheetCol<R>[],
    rows: R[],
    rankBy: (r: R) => number | null,
    rowAttrs: (r: R) => string,
    highlighted: (r: R) => boolean,
    onClick: (r: R) => void,
    tip: (r: R) => string,
    rowLabel: (r: R) => string,
    chartFallbackLabel: string,
  ): void {
    // Rank is always by the dataset's headline measure, whichever column the
    // user sorted by — so it stays "position in the country", not a row number.
    const ranked = [...rows]
      .filter((r) => rankBy(r) != null)
      .sort((a, b) => (rankBy(b) ?? 0) - (rankBy(a) ?? 0))
    const rankOf = new Map(ranked.map((r, i) => [r, i + 1]))

    sortRows(
      rows,
      cols.find((c) => c.k === SH.sort),
      SH.dir,
    )

    $('sh-thead').innerHTML =
      '<tr>' +
      cols
        .map(
          (c) =>
            `<th class="${c.cls ?? ''}" data-k="${c.k}"${
              SH.sort === c.k ? ` aria-sort="${SH.dir < 0 ? 'descending' : 'ascending'}"` : ''
            }>${c.lab}<span class="ar">${SH.dir < 0 ? '▼' : '▲'}</span></th>`,
        )
        .join('') +
      '</tr>'

    // One bar per row, measured on the column the table is sorted by when
    // that column is a number, and on the dataset's headline measure
    // otherwise (the name column, or no sort at all).
    const sortCol = cols.find((c) => c.k === SH.sort)
    const measure = sortCol?.key && SH.sort !== 'name' ? sortCol.key : rankBy
    const label = sortCol && SH.sort !== 'name' ? sortCol.lab : chartFallbackLabel
    paintSheetChart(
      rows.map((r) => {
        const value = measure(r)
        return { label: rowLabel(r), value: typeof value === 'number' ? value : null }
      }),
      label,
      '',
    )

    $('sh-tbody').innerHTML = rows
      .map(
        (r, i) =>
          `<tr${highlighted(r) ? ' class="hi"' : ''} data-i="${i}" ${rowAttrs(r)}>` +
          cols
            .map((c) =>
              c.k === 'rk'
                ? `<td><span class="sh-rk">${rankOf.get(r) ?? '–'}</span></td>`
                : `<td class="${c.cls ?? ''}">${c.cell(r)}</td>`,
            )
            .join('') +
          '</tr>',
      )
      .join('')

    $('sh-thead')
      .querySelectorAll<HTMLElement>('th')
      .forEach((th) => {
        const k = th.dataset['k']!
        const col = cols.find((c) => c.k === k)
        if (!col || col.sortable === false || !col.key) return
        th.addEventListener('click', () => {
          if (SH.sort === k) SH.dir = SH.dir === -1 ? 1 : -1
          else {
            SH.sort = k
            // Names read best A-Z; every measure reads best biggest-first.
            SH.dir = k === 'name' ? 1 : -1
          }
          renderSheet()
        })
      })

    $('sh-tbody')
      .querySelectorAll<HTMLElement>('tr')
      .forEach((tr) => {
        const row = (): R | undefined => rows[Number(tr.dataset['i'])]
        tr.addEventListener('click', () => {
          const r = row()
          if (r) onClick(r)
        })
        // The same card the map hover shows, on the row: the table answers
        // "how big" and "how fast", the card answers "compared to when" —
        // and for a settlement it is the only place its accommodation split
        // and its county's trend appear together.
        tr.addEventListener('pointerenter', (e) => {
          highlightSheetChart(Number(tr.dataset['i']))
          if (e.pointerType === 'touch') return
          const r = row()
          if (r) showTip(e.clientX, e.clientY, tip(r))
        })
        tr.addEventListener('pointermove', (e) => {
          if (e.pointerType === 'touch') return
          const r = row()
          if (r) showTip(e.clientX, e.clientY, tip(r))
        })
        tr.addEventListener('pointerleave', () => {
          highlightSheetChart(null)
          hideTip()
        })
      })
  }

  function renderSheet(): void {
    if (!SH.open) return
    const counties = SH.dataset === 'counties'
    $('sh-title').textContent =
      SH.dataset === 'counties'
        ? 'Vármegyék fejlődése és kapacitása'
        : SH.dataset === 'settlements'
          ? 'Kiemelt települések kimutatása'
          : SH.dataset === 'ages'
            ? 'Kik utaznak — korcsoportok'
            : SH.direction === 'out'
              ? 'Hova utazunk — magyar kiutazások'
              : SH.direction === 'home'
                ? 'Hova utazunk itthon — belföldi utazások'
                : 'Miért jönnek — utazási célok'
    // The accommodation/population switch only means anything for settlements,
    // the trip-length switch only for motivations.
    $('sh-seg').hidden = SH.dataset !== 'settlements'
    $('sh-seg-trip').hidden = SH.dataset !== 'motivations' || SH.direction === 'home'
    $('sh-seg-dir').hidden = SH.dataset !== 'motivations'
    $('sh-seg-dest').hidden = SH.dataset !== 'ages'
    // The private/business colour key belongs to the accommodation columns.
    $('sh-legend').hidden = SH.dataset === 'motivations' || SH.dataset === 'ages'

    if (SH.dataset === 'motivations') {
      renderMotivationSheet()
      return
    }
    if (SH.dataset === 'ages') {
      renderAgeSheet()
      return
    }

    if (counties) {
      renderSheetTable(
        COUNTY_COLS,
        [...INDICATORS.counties],
        (r) => r.nights,
        (r) => `data-county="${r.slug}"`,
        (r) => S.sel?.type === 'county' && S.sel.id === r.slug,
        (r) => {
          if (S.view !== 'stay') setView('stay')
          select({ type: 'county', id: r.slug })
          renderSheet()
        },
        (r) => tipCounty(r.slug),
        (r) => r.name,
        'Vendégéjszaka',
      )
      const beds = INDICATORS.counties.reduce((a, c) => a + (c.capacity ?? 0), 0)
      $('sh-sub').textContent =
        `20 vármegye · ${INDICATORS.from}→${INDICATORS.to} · KSH vendégforgalom és férőhely ` +
        `(július 31-i állapot) · kattints egy sorra a térképi kijelöléshez`
      $('sh-tot').innerHTML =
        `Országos férőhely ${INDICATORS.to}: <strong>${nf.format(beds)}</strong> · ` +
        `főszezoni kihasználtság mediánja: <strong>${
          INDICATORS.medianOccupancyPeak == null
            ? 'n. a.'
            : nf1.format(INDICATORS.medianOccupancyPeak) + ' %'
        }</strong> — a jelzés ehhez és a kereslet/kínálat különbségéhez képest készül`
      return
    }

    const rows = settlementRows()
    renderSheetTable(
      SETTLEMENT_COLS,
      rows,
      (r) => r.val,
      (r) => `data-county="${r.s.countySlug}" data-slug="${r.s.slug}"`,
      (r) => S.focusSettlement === r.s.slug,
      (r) => {
        if (S.view !== 'stay') setView('stay')
        // select() toggles a re-clicked id off — only call it to switch
        // counties; refocusing a different settlement within the county that
        // is already selected must not deselect it.
        if (S.sel?.type !== 'county' || S.sel.id !== r.s.countySlug) {
          select({ type: 'county', id: r.s.countySlug })
        }
        const bar = barById.get(r.s.slug)
        if (bar) {
          S.focusSettlement = r.s.slug
          flyTo(settlementView(bar))
          setTargets()
          renderSel()
        }
        renderSheet()
      },
      (r) =>
        tipSettlement({
          name: r.s.name,
          countySlug: r.s.countySlug,
          population: r.s.population,
          accommodations: r.acc,
        }),
      (r) => r.s.name,
      SH.mode === 'pop' ? 'Lakosság' : 'Szálláshely',
    )
    const tPop = rows.reduce((a, r) => a + r.s.population, 0)
    const tAcc = rows.reduce((a, r) => a + r.acc.total, 0)
    const an = nationalAccommodations()
    $('sh-sub').textContent =
      `${rows.length} kiemelt település · KSH Helységnévtár (népesség) + szallashelyminosites.hu ` +
      `(szálláshely, élő nyilvántartás) · kattints egy sorra a térképi fókuszhoz`
    $('sh-tot').innerHTML =
      `Táblázat összesen: <strong>${nf.format(tPop)}</strong> lakos · <strong>${nf.format(
        tAcc,
      )}</strong> szálláshely — országosan <strong>${nf.format(
        an.private + an.nonPrivate,
      )}</strong> (${nf.format(an.private)} magán / ${nf.format(an.nonPrivate)} nem magán)`
  }

  function renderMotivationSheet(): void {
    if (SH.direction === 'home') {
      renderDomesticSheet()
      return
    }

    const rows = motivationRows()
    const window = travelWindow(motivationProbe())
    const outbound = SH.direction === 'out'

    renderSheetTable(
      MOTIVATION_COLS,
      rows,
      // Ranked on trips, but only within one level: a total and its own parts
      // ranked against each other would put "Összesen" first and every real
      // category behind it.
      (r) => (r.level === (outbound ? 0 : 1) && r.key !== 'osszesen' ? r.trips : null),
      () => '',
      () => false,
      // Nothing on the map corresponds to a purpose, so a row is not a link.
      () => {},
      (r) => tipMotivation(r),
      (r) => r.label,
      'Utazások száma',
    )

    const lengthLabel = SH.tripLength === 'oneDay' ? 'Egynapos' : 'Többnapos'
    const who = outbound ? 'magyar kiutazás' : 'beutazás'
    $('sh-sub').textContent = window
      ? `${lengthLabel} ${who} · ${window.label}${window.fallback ? ' (a legutóbbi közölt negyedév)' : ''} · ` +
        `KSH határmegfigyelés (${outbound ? 'tur0047' : 'tur0045'}) — utazás és költés, nem vendégéjszaka`
      : 'Nincs betöltve utazási adat (pnpm data:travel).'

    const total = rows.find((r) => r.key === 'osszesen')
    $('sh-tot').innerHTML = total
      ? `${lengthLabel} utazás összesen: <strong>${
          total.trips == null ? 'n. a.' : `${nf.format(Math.round(total.trips))} ezer`
        }</strong> · költés <strong>${
          total.spending == null
            ? 'n. a.'
            : `${nf.format(Math.round(total.spending))} millió Ft</strong>`
        } · egy főre naponta <strong>${
          total.perDay == null ? 'n. a.' : `${nf1.format(total.perDay)} ezer Ft`
        }</strong> — a vendégéjszaka-adattal nem összeadható, más felmérés`
      : ''
  }

  /**
   * The third population: residents travelling inside Hungary, by the region
   * they went to. Trips and days only — KSH publishes no spending split by
   * destination region, and there is no honest way to invent one.
   */
  function renderDomesticSheet(): void {
    const rows = domesticRows()
    const window = travelWindow()

    renderSheetTable(
      DOMESTIC_COLS,
      rows,
      (r) => r.trips,
      () => '',
      () => false,
      () => {},
      (r) =>
        tipHead(r.label, window?.label ?? '') +
        `<div class="tt-rows">` +
        tipRow('utazás', r.trips == null ? 'n. a.' : `${nf.format(Math.round(r.trips))} ezer`) +
        tipRow(
          'eltöltött idő',
          r.days == null ? 'n. a.' : `${nf.format(Math.round(r.days))} ezer nap`,
        ) +
        tipRow('átlagos hossz', r.avgStay == null ? 'n. a.' : `${nf1.format(r.avgStay)} nap`) +
        `</div>` +
        `<div class="p">KSH háztartási felmérés (tur0051/tur0052) · többnapos belföldi utazás</div>`,
      (r) => r.label,
      'Utazások száma',
    )

    $('sh-sub').textContent = window
      ? `Többnapos belföldi utazások célrégió szerint · ${window.label}` +
        `${window.fallback ? ' (a legutóbbi közölt negyedév)' : ''} · KSH tur0051/tur0052`
      : 'Nincs betöltve utazási adat (pnpm data:travel).'

    const trips = rows.reduce((sum, r) => sum + (r.trips ?? 0), 0)
    const days = rows.reduce((sum, r) => sum + (r.days ?? 0), 0)
    $('sh-tot').innerHTML =
      `Összesen <strong>${nf.format(Math.round(trips))} ezer</strong> belföldi utazás · ` +
      `<strong>${nf.format(Math.round(days))} ezer nap</strong> — a magyar lakosság saját utazásai, ` +
      `nem a szálláshelyek vendégforgalma`
  }

  function setSheet(on: boolean): void {
    SH.open = on
    $('sheetwrap').hidden = !on
    if (on) {
      setPlay(false)
      renderSheet()
    }
  }
  $('btn-sheet').addEventListener('click', () => setSheet(true))
  $('sh-close').addEventListener('click', () => setSheet(false))
  // A row tooltip anchored to a row the user has just scrolled away from
  // would hang in mid-air, so the scroll dismisses it.
  $('sh-scroll').addEventListener('scroll', hideTip, { passive: true })
  $('sheetwrap').addEventListener('click', (e) => {
    if (e.target === $('sheetwrap')) setSheet(false)
  })
  document.querySelectorAll<HTMLButtonElement>('#sh-tabs button').forEach((b) => {
    b.addEventListener('click', () => {
      SH.dataset = b.dataset['shd'] as ShDataset
      // Each dataset gets its own default sort; carrying the other one's
      // column over would silently leave the table unsorted.
      // Motivations open unsorted on purpose: the rows are a hierarchy, and
      // any sort flattens a total in among its own parts. Clicking a column
      // header still sorts, which is then the user's explicit choice.
      SH.sort = SH.dataset === 'counties' ? 'growth' : SH.dataset === 'motivations' ? '' : SH.mode
      SH.dir = -1
      document
        .querySelectorAll<HTMLButtonElement>('#sh-tabs button')
        .forEach((x) => x.setAttribute('aria-pressed', String(x.dataset['shd'] === SH.dataset)))
      renderSheet()
    })
  })
  document.querySelectorAll<HTMLButtonElement>('#sh-seg-dest button').forEach((b) => {
    b.addEventListener('click', () => {
      SH.destination = b.dataset['shdest'] as AgeDestination
      document
        .querySelectorAll<HTMLButtonElement>('#sh-seg-dest button')
        .forEach((x) =>
          x.setAttribute('aria-pressed', String(x.dataset['shdest'] === SH.destination)),
        )
      renderSheet()
    })
  })
  document.querySelectorAll<HTMLButtonElement>('#sh-seg-dir button').forEach((b) => {
    b.addEventListener('click', () => {
      SH.direction = b.dataset['shdir'] as 'in' | 'out' | 'home'
      // Each direction has its own columns and its own hierarchy, so the sort
      // starts over rather than pointing at a column that may not exist.
      SH.sort = SH.direction === 'home' ? 'trips' : ''
      SH.dir = -1
      document
        .querySelectorAll<HTMLButtonElement>('#sh-seg-dir button')
        .forEach((x) => x.setAttribute('aria-pressed', String(x.dataset['shdir'] === SH.direction)))
      renderSheet()
    })
  })
  document.querySelectorAll<HTMLButtonElement>('#sh-seg-trip button').forEach((b) => {
    b.addEventListener('click', () => {
      SH.tripLength = b.dataset['shl'] as TripLength
      document
        .querySelectorAll<HTMLButtonElement>('#sh-seg-trip button')
        .forEach((x) => x.setAttribute('aria-pressed', String(x.dataset['shl'] === SH.tripLength)))
      renderSheet()
    })
  })
  document.querySelectorAll<HTMLButtonElement>('#sh-seg button').forEach((b) => {
    b.addEventListener('click', () => {
      SH.mode = b.dataset['shm'] as 'accom' | 'pop'
      SH.sort = SH.mode
      SH.dir = -1
      document
        .querySelectorAll<HTMLButtonElement>('#sh-seg button')
        .forEach((x) => x.setAttribute('aria-pressed', String(x.dataset['shm'] === SH.mode)))
      renderSheet()
    })
  })

  // ---- raycasting -------------------------------------------------------

  const ray = new THREE.Raycaster()
  const mouse = new THREE.Vector2()
  let hovered: THREE.Object3D | null = null
  let downPos: [number, number] | null = null
  let hoverSet: THREE.Mesh[] = []
  const regionById = new Map<string, THREE.Mesh>()
  const barById = new Map<string, THREE.Mesh>()
  countyMeshes.forEach((m) => regionById.set((m.userData as RegionUserData).id!, m))
  countryMeshes.forEach((m) => regionById.set((m.userData as RegionUserData).id!, m))
  countyBars.forEach((b) => barById.set((b.userData as BarUserData).id, b))
  countryBars.forEach((b) => barById.set((b.userData as BarUserData).id, b))
  settlementBars.forEach((b) => barById.set((b.userData as BarUserData).id, b))

  function clearHover(): void {
    for (const o of hoverSet) {
      ;(o.material as THREE.MeshLambertMaterial).emissive.setHex(0)
      const ud = o.userData as RegionUserData
      if (ud.kind === 'county' || ud.kind === 'country') ud.liftT = 0
    }
    hoverSet = []
    hovered = null
  }
  function setHover(o: THREE.Object3D): void {
    clearHover()
    hovered = o
    const id = (o.userData as RegionUserData | BarUserData).id
    if (!id) return
    const r = regionById.get(id)
    const b = barById.get(id)
    if (r) {
      ;(r.material as THREE.MeshLambertMaterial).emissive.setHex(0x262019)
      const rud = r.userData as RegionUserData
      if (rud.kind === 'county' && !(S.sel && S.sel.id === rud.id)) rud.liftT = 1.15
      else if (rud.kind === 'country') rud.liftT = S.ext === 'world' ? 5 : 2.6
      hoverSet.push(r)
    }
    if (b && b.visible) {
      ;(b.material as THREE.MeshLambertMaterial).emissive.setHex(0x40251c)
      hoverSet.push(b)
    }
  }
  function pick(e: PointerEvent): THREE.Object3D | null {
    mouse.set((e.clientX / innerWidth) * 2 - 1, -(e.clientY / innerHeight) * 2 + 1)
    ray.setFromCamera(mouse, camera)
    const hits = ray.intersectObjects(pickables, false).filter((x) => x.object.visible)
    return hits.length ? hits[0]!.object : null
  }
  /**
   * Point the hover highlight and tooltip at whatever is under `e`.
   *
   * Driven by pointermove for a mouse, but by the tap itself for touch: a
   * finger has no hover, and running this on touch moves would make the
   * tooltip chase the finger around while it is rotating the map.
   */
  function applyHover(e: PointerEvent): void {
    const o = pick(e)
    const ud = o?.userData as (RegionUserData | BarUserData) | undefined
    if (ud && (ud.kind === 'county' || ud.kind === 'countyBar') && S.view === 'stay') {
      setHover(o!)
      showTip(e.clientX, e.clientY, tipCounty(ud.id!))
      canvas.style.cursor = 'pointer'
    } else if (ud && (ud.kind === 'country' || ud.kind === 'countryBar') && S.view === 'origin') {
      setHover(o!)
      showTip(e.clientX, e.clientY, tipCountry(ud.id!))
      canvas.style.cursor = 'pointer'
    } else if (ud && ud.kind === 'settlementBar' && S.view === 'stay') {
      setHover(o!)
      showTip(e.clientX, e.clientY, tipSettlement(ud as BarUserData))
      canvas.style.cursor = 'pointer'
    } else if (ud && (ud.kind === 'county' || ud.kind === 'countyBar') && S.view === 'origin') {
      clearHover()
      showTip(
        e.clientX,
        e.clientY,
        '<div class="t">Magyarország</div>a célország — váltás: „Hol szállnak meg?”',
      )
      canvas.style.cursor = 'default'
    } else {
      clearHover()
      hideTip()
      canvas.style.cursor = 'default'
    }
  }
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return
    applyHover(e)
  })
  canvas.addEventListener('pointerdown', (e) => {
    downPos = [e.clientX, e.clientY]
  })
  canvas.addEventListener('pointerup', (e) => {
    if (!downPos) return
    const moved = Math.hypot(e.clientX - downPos[0], e.clientY - downPos[1])
    downPos = null
    if (moved > 6) return
    const o = pick(e)
    const ud = o?.userData as (RegionUserData | BarUserData) | undefined
    if (ud && ud.kind === 'settlementBar' && S.view === 'stay') {
      const bud = ud as BarUserData
      if (S.focusSettlement === bud.id) {
        S.focusSettlement = null
        flyTo(S.sel?.type === 'county' ? countyView(S.sel.id) : curView())
      } else {
        S.focusSettlement = bud.id
        flyTo(settlementView(o!))
      }
      setTargets()
      renderSel()
    } else if (ud && (ud.kind === 'county' || ud.kind === 'countyBar') && S.view === 'stay') {
      // Clicking the county itself while one of its settlements is focused
      // just backs out to the county view, mirroring how the settlement
      // pin toggles off on a second click — a full select() here would
      // instead deselect the county entirely.
      if (S.focusSettlement != null && S.sel?.type === 'county' && S.sel.id === ud.id) {
        S.focusSettlement = null
        flyTo(countyView(ud.id!))
        setTargets()
        renderSel()
      } else {
        select({ type: 'county', id: ud.id! })
      }
    } else if (ud && (ud.kind === 'country' || ud.kind === 'countryBar') && S.view === 'origin') {
      select({ type: 'country', id: ud.id! })
    } else select(null)
    // Touch has no hover to drive the tooltip, so the tap does it — including
    // dismissing it when the tap landed on empty map.
    if (e.pointerType !== 'mouse') applyHover(e)
  })
  canvas.addEventListener('pointerleave', () => {
    clearHover()
    hideTip()
  })

  // ---- timeline ---------------------------------------------------------

  const slider = $<HTMLInputElement>('slider')
  function tlSetup(): void {
    slider.max = String((S.gran === 'm' ? NM : NY) - 1)
    slider.value = String(idx())
    const ticks = $('tl-ticks')
    ticks.innerHTML = ''
    const years =
      S.gran === 'm'
        ? [...new Set(monthlyPeriods.map((p) => p.slice(0, 4)))]
        : yearlyPeriods.filter((_, i) => i % 3 === 0)
    years.forEach((y) => {
      const s = document.createElement('span')
      s.textContent = `’${y.slice(2)}`
      ticks.appendChild(s)
    })
    $('tl-rangecap').textContent =
      S.gran === 'm'
        ? `${formatMonthlyShort(monthlyPeriods[0]!)} – ${formatMonthlyShort(monthlyPeriods.at(-1)!)} · havi`
        : `${yearlyPeriods[0]} – ${yearlyPeriods.at(-1)} · éves`
    tlPaint()
  }
  function tlPaint(): void {
    slider.value = String(idx())
    const pct = (idx() / Number(slider.max)) * 100
    slider.style.setProperty(
      '--tl-bg',
      `linear-gradient(90deg,var(--color-petrol) ${pct}%,var(--color-sand) ${pct}%)`,
    )
    $('tl-period').textContent = periodLabel()
  }
  slider.addEventListener('input', () => {
    if (S.gran === 'm') S.mi = Number(slider.value)
    else S.yi = Number(slider.value)
    update()
  })
  function step(d: number): void {
    if (S.gran === 'm') S.mi = Math.max(0, Math.min(NM - 1, S.mi + d))
    else S.yi = Math.max(0, Math.min(NY - 1, S.yi + d))
    update()
  }
  $('btn-prev').addEventListener('click', () => step(-1))
  $('btn-next').addEventListener('click', () => step(1))
  let timer: number | null = null
  function setPlay(on: boolean): void {
    if (on && timer) return
    if (on) {
      if (idx() >= Number(slider.max)) {
        if (S.gran === 'm') S.mi = 0
        else S.yi = 0
      }
      timer = window.setInterval(
        () => {
          if (idx() >= Number(slider.max)) {
            setPlay(false)
            return
          }
          step(1)
        },
        reduced ? 950 : 700,
      )
    } else if (timer) {
      clearInterval(timer)
      timer = null
    }
    $('ic-play').style.display = timer ? 'none' : 'block'
    $('ic-pause').style.display = timer ? 'block' : 'none'
    $('btn-play').setAttribute('aria-label', timer ? 'Szünet' : 'Lejátszás')
  }
  $('btn-play').addEventListener('click', () => setPlay(!timer))
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') select(null)
    if (e.key === 'ArrowRight') step(1)
    if (e.key === 'ArrowLeft') step(-1)
  })

  // ---- controls / views ---------------------------------------------------

  $('tab-stay').addEventListener('click', () => setView('stay'))
  $('tab-origin').addEventListener('click', () => setView('origin'))
  function setView(v: View): void {
    if (S.view === v) return
    S.view = v
    S.sel = null
    setPlay(false)
    flyTo(curView())
    update()
    renderSel()
  }
  document.querySelectorAll<HTMLButtonElement>('#seg-gran button').forEach((b) => {
    b.addEventListener('click', () => {
      const g = b.dataset['gran'] as Gran
      if (S.gran === g) return
      switchGranularity(g)
      setPlay(false)
      tlSetup()
      update()
    })
  })
  document.querySelectorAll<HTMLButtonElement>('#seg-scope button').forEach((b) => {
    b.addEventListener('click', () => {
      S.scope = b.dataset['scope'] as Scope
      update()
    })
  })
  /**
   * Newest index in the current granularity that actually has a bed-place
   * figure behind it.
   *
   * KSH publishes capacity once a year, so the monthly series runs ahead of
   * it — at the time of writing, nights reach 2026-06 while capacity stops at
   * 2025. Carrying the last known capacity forward would quietly present a
   * 2025 denominator as if it were 2026's, so instead the timeline moves to
   * where the real figure is.
   */
  function lastPeriodWithCapacity(gran: Gran): number | null {
    const periods = gran === 'm' ? monthlyPeriods : yearlyPeriods
    for (let i = periods.length - 1; i >= 0; i--) {
      if (DATA.capacity[periods[i]!.slice(0, 4)]) return i
    }
    return null
  }

  $<HTMLSelectElement>('sel-metric').addEventListener('change', (e) => {
    S.metric = (e.target as HTMLSelectElement).value as Metric
    // Switching to a supply-side metric while parked on a period that has no
    // capacity would blank the map for no visible reason.
    if (SCOPE_FREE_METRICS.has(S.metric)) {
      const periods = S.gran === 'm' ? monthlyPeriods : yearlyPeriods
      const cur = periods[idx()]
      if (cur && !DATA.capacity[cur.slice(0, 4)]) {
        const target = lastPeriodWithCapacity(S.gran)
        if (target != null) {
          if (S.gran === 'm') S.mi = target
          else S.yi = target
        }
      }
    }
    update()
  })
  document.querySelectorAll<HTMLButtonElement>('#seg-flow button').forEach((b) => {
    b.addEventListener('click', () => {
      const flow = b.dataset['flow'] as Flow
      if (S.flow === flow) return
      S.flow = flow
      // The two directions have different value ranges and different units,
      // so the legend's own scale has to be rebuilt, which update() does.
      update()
    })
  })
  document.querySelectorAll<HTMLButtonElement>('#seg-extent button').forEach((b) => {
    b.addEventListener('click', () => {
      const ext = b.dataset['ext'] as Extent
      if (S.ext === ext) return
      S.ext = ext
      flyTo(curView())
      update()
    })
  })
  $<HTMLInputElement>('chk-arcs').addEventListener('change', (e) => {
    S.arcs = (e.target as HTMLInputElement).checked
    rebuildArcs()
  })
  $<HTMLInputElement>('chk-delta').addEventListener('change', (e) => {
    S.delta = (e.target as HTMLInputElement).checked
    setTargets()
  })

  /**
   * Publish the two chrome heights the fixed-position layout has to work
   * around. Both are measured rather than assumed: the header wraps to two or
   * three rows on narrow screens and the timeline's controls wrap too, so a
   * hard-coded offset put the map options on top of the tabs and pushed the
   * panel off the top of the screen on a landscape phone.
   */
  function syncLayout(): void {
    const tl = document.querySelector<HTMLElement>('.tl-inner')
    if (tl) document.documentElement.style.setProperty('--tl-h', `${tl.offsetHeight + 14}px`)
    const hud = document.querySelector<HTMLElement>('.tophud')
    if (hud) document.documentElement.style.setProperty('--hud-h', `${hud.offsetHeight}px`)
    const opts = document.querySelector<HTMLElement>('.mapopts')
    // offsetHeight is 0 when every control inside is hidden (origin view), so
    // the band correctly costs nothing then.
    if (opts) document.documentElement.style.setProperty('--opt-h', `${opts.offsetHeight + 8}px`)
  }
  addEventListener('resize', syncLayout)
  // Wrapping changes an element's height without changing the window's, so
  // resize alone misses it.
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(() => syncLayout())
    for (const sel of ['.tl-inner', '.tophud', '.mapopts']) {
      const el = document.querySelector(sel)
      if (el) ro.observe(el)
    }
  }
  // Once synchronously as well: every other path here (rAF, ResizeObserver)
  // is driven by the rendering loop, which does not run at all while the tab
  // is hidden — leaving the panel and map options on their fallback offsets
  // until the first frame.
  syncLayout()
  function syncControls(): void {
    $('tab-stay').setAttribute('aria-selected', String(S.view === 'stay'))
    $('tab-origin').setAttribute('aria-selected', String(S.view === 'origin'))
    document
      .querySelectorAll<HTMLButtonElement>('#seg-gran button')
      .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset['gran'] === S.gran)))
    document
      .querySelectorAll<HTMLButtonElement>('#seg-scope button')
      .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset['scope'] === S.scope)))
    document
      .querySelectorAll<HTMLButtonElement>('#seg-extent button')
      .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset['ext'] === S.ext)))
    $<HTMLSelectElement>('sel-metric').value = S.metric
    const stay = S.view === 'stay'
    $('seg-extent').hidden = stay
    $('seg-flow').hidden = stay || !DATA.travel
    $('chk-arcs-wrap').hidden = stay
    document
      .querySelectorAll<HTMLButtonElement>('#seg-flow button')
      .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset['flow'] === S.flow)))
    // Restored state has to reach the checkboxes too, or a remembered "arcs
    // off" would show a ticked box over an empty map.
    $<HTMLInputElement>('chk-arcs').checked = S.arcs
    $<HTMLInputElement>('chk-delta').checked = S.delta
    // Capacity has no domestic/foreign split, so offering the selector for it
    // would imply a breakdown that does not exist.
    const scopeApplies = stay && !SCOPE_FREE_METRICS.has(S.metric)
    $('seg-scope').style.display = scopeApplies ? '' : 'none'
    $('sel-metric').style.display = stay ? '' : 'none'
    $('map-title').textContent = stay
      ? MAP_TITLE[S.metric]
      : S.flow === 'out'
        ? `Magyar utazások célországok szerint (ezer út)${
            purposeFiltered() ? ' · becsült, szűrt cél szerint' : ''
          }`
        : 'Külföldi vendégéjszakák küldő országok szerint'
    const curPeriod = (S.gran === 'm' ? monthlyPeriods : yearlyPeriods)[idx()]
    const capMissing =
      SCOPE_FREE_METRICS.has(S.metric) && !!curPeriod && !DATA.capacity[curPeriod.slice(0, 4)]
    $('map-sub').textContent = stay
      ? `${periodLabel()} · ${
          capMissing
            ? 'erre az évre még nincs KSH férőhelyadat'
            : scopeApplies
              ? SCOPE_LABEL[S.scope]
              : 'KSH férőhelyadat (júl. 31.)'
        }`
      : `${periodLabel()} · a KSH kiemelt országai`
    requestAnimationFrame(syncLayout)
  }
  function update(): void {
    syncControls()
    tlPaint()
    writeURL()
    setTargets()
    paintLegend()
    renderKpis()
    renderSpending()
    renderOutboundPurposes()
    renderEconomy()
    placeAuxCards()
    renderTop()
    if (S.sel) renderSel()
  }

  // ---- animation loop -----------------------------------------------------

  const clock = new THREE.Clock()
  function tick(): void {
    requestAnimationFrame(tick)
    const dt = Math.min(clock.getDelta(), 0.1)
    const f = reduced ? 1 : 1 - Math.exp(-dt * 7)
    if (camAnim) {
      camAnim.t += dt / camAnim.dur
      const u = Math.min(1, camAnim.t)
      const s = u * u * u * (u * (u * 6 - 15) + 10)
      camera.position.lerpVectors(camAnim.p0, camAnim.p1, s)
      controls.target.lerpVectors(camAnim.t0, camAnim.t1, s)
      if (u >= 1) camAnim = null
    }
    for (const m of countyMeshes) {
      const ud = m.userData as RegionUserData
      ;(m.material as THREE.MeshLambertMaterial).color.lerp(ud.tc, f)
      m.position.z += ((ud.liftT ?? 0) - m.position.z) * f
    }
    for (const mt of countyLineMats) mt.opacity += ((mt.userData['to'] as number) - mt.opacity) * f
    for (const mt of settlementLineMats)
      mt.opacity += ((mt.userData['to'] as number) - mt.opacity) * f
    for (const m of countryMeshes) {
      const ud = m.userData as RegionUserData
      ;(m.material as THREE.MeshLambertMaterial).color.lerp(ud.tc, f)
      m.position.z += ((ud.liftT ?? 0) - m.position.z) * f
    }
    // Progress of the camera flight, if one is running — drives the bar
    // growth curve below.
    const camU = camAnim ? Math.min(1, camAnim.t) : null
    for (const b of allBars) {
      const bud = b.userData as BarUserData
      /*
       * Bar height. Normally exponential smoothing, which is fastest at the
       * very start — fine on its own, wrong while the camera is moving. On
       * deselecting a county every county's column comes back at once, and at
       * that speed they reached full height in ~0.4s while the 1.6s flight
       * still had the camera pulled in close, so the tall ones shot off the
       * top of the screen before the wide view caught up.
       *
       * So while a flight is running, a bar that has to GROW follows the
       * flight's own progress on an ease-in curve instead: slow to start, fast
       * to finish, arriving as the camera does. Shrinking keeps the snappy
       * exponential — a bar getting out of the way early never looks wrong,
       * and slowing it down would leave tall bars hanging in frame while the
       * camera moves in.
       */
      const h =
        camU != null && bud.h0 != null && bud.th > bud.h0
          ? bud.h0 + (bud.th - bud.h0) * (camU * camU)
          : b.scale.y + (bud.th - b.scale.y) * f
      b.scale.y = h
      b.visible = h > 0.05
      const mat = b.material as THREE.MeshLambertMaterial
      mat.opacity += ((bud.toOp ?? 1) - mat.opacity) * f
      const r = b.scale.x + (bud.tr - b.scale.x) * f
      b.scale.x = r
      b.scale.z = r
      const L = bud.label
      if (L) {
        L.visible = b.visible && !!bud.show
        if (L.visible) {
          const hot = hovered != null && (hovered.userData as BarUserData).id === bud.id
          const sT = (L.userData['s0'] as number) * (hot ? 1.9 : 1)
          L.scale.x += (sT - L.scale.x) * f
          L.scale.y = L.scale.x * 0.375
          const lmat = L.material as THREE.SpriteMaterial
          lmat.opacity +=
            ((hot ? 1 : 0.45) * (bud.toOp ?? 1) - lmat.opacity) * (reduced ? 1 : f * 1.6)
          lmat.depthTest = !hot
          L.renderOrder = hot ? 10 : 1
          L.position.set(
            b.position.x,
            b.position.y + h + (bud.kind === 'countyBar' ? 2.4 : 9),
            b.position.z,
          )
        }
      }
      mat.color.lerp(bud.tc, f)
    }
    const arcs = arcGroup.children as THREE.Mesh[]
    if (arcs.length && !reduced) {
      const selIso = S.sel?.type === 'country' ? S.sel.id : null
      if (selIso) {
        const j = arcs.findIndex((a) => a.userData['iso'] === selIso)
        if (j >= 0) arcSel = j
      }
      arcT += dt
      const CY = 1.5
      if (arcT >= CY) {
        arcT = 0
        if (!selIso) arcSel = (arcSel + 1) % arcs.length
      }
      arcs.forEach((a, j) => {
        a.userData['to'] = j === arcSel ? (selIso ? 0.95 : 0.85) : selIso ? 0.06 : 0.11
        const mat = a.material as THREE.MeshBasicMaterial
        mat.opacity += ((a.userData['to'] as number) - mat.opacity) * f * 1.4
      })
      const actIso = selIso ?? arcs[arcSel]?.userData['iso'] ?? null
      if (actIso)
        for (const b of countryBars)
          (b.userData as BarUserData).toOp =
            (b.userData as BarUserData).id === actIso ? 1 : selIso ? 0.22 : 0.35
      const a = arcs[arcSel]
      if (a) {
        const u = Math.min(1, arcT / (CY * 0.8))
        const e = u * u * (3 - 2 * u)
        pulse.position.copy((a.userData['curve'] as THREE.QuadraticBezierCurve3).getPoint(e))
        const s = Math.max(1.2, (a.userData['w'] as number) * 2.6)
        pulse.scale.set(s, s, s)
        ;(pulse.material as THREE.MeshBasicMaterial).opacity = 0.95 * (1 - Math.abs(e - 0.5) * 0.5)
        pulse.visible = true
      }
    } else {
      for (const a of arcs) {
        const mat = a.material as THREE.MeshBasicMaterial
        mat.opacity += ((reduced ? 0.3 : (a.userData['to'] as number)) - mat.opacity) * f
      }
      pulse.visible = false
    }
    tuneNear()
    controls.update()

    /*
     * While the drawer slides, the scene gives up every second frame. The
     * map is static during those 280ms (a camera flight is the one exception,
     * and it keeps every frame), and halving the GPU work there is what makes
     * the difference between a drawer that slides and one that appears to
     * teleport on a busy machine. The lerps above still run every frame, so
     * nothing jumps when the full rate comes back.
     */
    frame += 1
    if (drawerSliding() && camAnim === null && frame % 2 === 1) return
    renderer.render(scene, camera)
  }
  let frame = 0
  function resize(): void {
    camera.aspect = innerWidth / innerHeight
    camera.updateProjectionMatrix()
    renderer.setSize(innerWidth, innerHeight)
  }
  addEventListener('resize', resize)
  resize()

  // ---- init -----------------------------------------------------------------

  $('chip-info').textContent =
    `Havi: ${monthlyPeriods[0]}–${monthlyPeriods.at(-1)} · Éves: ${yearlyPeriods[0]}–${yearlyPeriods.at(-1)} · KSH STADAT · kattints egy megyére a településekért`

  tlSetup()
  update()
  const v0 = curView()
  camera.position.copy(
    camPosFor({ ...v0, dist: v0.dist * 3.2, elev: Math.min(1.35, v0.elev + 0.25) }),
  )
  controls.target.copy(v0.target)
  flyTo(v0)
  function applyTheme(): void {
    const T = theme()
    ;(scene.background as THREE.Color).set(T.bg)
    ;(scene.fog as THREE.Fog).color.set(T.bg)
    ;(ocean.material as THREE.MeshLambertMaterial).color.set(T.ocean)
    for (const m of landMeshes) {
      const ud = m.userData as RegionUserData
      ud.tc.set(T.land)
      ;(m.material as THREE.MeshLambertMaterial).color.set(T.land)
    }
    setTargets()
  }
  DARKMQ.addEventListener('change', applyTheme)
  applyTheme()
  tick()

  // Debug hook for support/QA — mirrors the design reference's own `__hv`.
  // Deliberately minimal: read-only snapshots, no mutation surface beyond `go`.
  ;(window as unknown as { __hv: unknown }).__hv = {
    state: S,
    settlementCount: settlementBars.length,
    counties: () =>
      countyBars.map((b) => {
        const ud = b.userData as BarUserData
        return {
          id: ud.id,
          name: ud.name,
          scaleY: b.scale.y,
          th: ud.th,
          scaleXZ: b.scale.x,
          tr: ud.tr,
        }
      }),
    settlements: () =>
      settlementBars.map((b) => {
        const ud = b.userData as BarUserData
        return {
          name: ud.name,
          population: ud.population,
          visible: b.visible,
          opacity: (b.material as THREE.MeshLambertMaterial).opacity,
          th: ud.th,
        }
      }),
    go: (slug: string) => select({ type: 'county', id: slug }),
    focus: (name: string) => {
      const bar = settlementBars.find((b) => (b.userData as BarUserData).name === name)
      if (!bar) return false
      const bud = bar.userData as BarUserData
      if (S.sel?.type !== 'county' || S.sel.id !== bud.countySlug)
        select({ type: 'county', id: bud.countySlug! })
      S.focusSettlement = bud.id
      flyTo(settlementView(bar))
      setTargets()
      renderSel()
      return true
    },
    tooltip: (name: string) => {
      const bar = settlementBars.find((b) => (b.userData as BarUserData).name === name)
      return bar ? tipSettlement(bar.userData as BarUserData) : null
    },
    camTarget: () => ({ x: controls.target.x, z: controls.target.z }),
    /** Country extent + the view framing it, for diagnosing camera fit. */
    huFraming: () => {
      const v = VIEWS.hu()
      // Where the country's real bounding box actually lands on screen —
      // projected from the corners rather than sampled from settlement pins,
      // which are not symmetric within the country.
      const xs: number[] = []
      const ys: number[] = []
      for (const bx of [huBounds[0][0], huBounds[1][0]]) {
        for (const bz of [huBounds[0][1], huBounds[1][1]]) {
          const p = new THREE.Vector3(bx - HXY[0], 0, bz - HXY[1]).project(camera)
          xs.push(((p.x + 1) / 2) * innerWidth)
          ys.push(((1 - p.y) / 2) * innerHeight)
        }
      }
      return {
        boundsW: huBounds[1][0] - huBounds[0][0],
        boundsH: huBounds[1][1] - huBounds[0][1],
        dist: v.dist,
        targetX: v.target.x,
        targetZ: v.target.z,
        camDist: camera.position.distanceTo(controls.target),
        screen: {
          x0: Math.min(...xs),
          x1: Math.max(...xs),
          y0: Math.min(...ys),
          y1: Math.max(...ys),
        },
      }
    },
    countyViewTarget: (slug: string) => {
      const v = countyView(slug)
      return { x: v.target.x, z: v.target.z, dist: v.dist }
    },
    countyCentroid: (slug: string) => {
      const b = pathW.bounds(countyFeatures[countyTopoIdx.get(slug)!] as never)
      return { x: (b[0][0] + b[1][0]) / 2 - HXY[0], z: (b[0][1] + b[1][1]) / 2 - HXY[1] }
    },
    screenPos: (name: string) => {
      const bar = settlementBars.find((b) => (b.userData as BarUserData).name === name)
      if (!bar) return null
      const v = bar.position.clone().project(camera)
      return {
        x: Math.round(((v.x + 1) / 2) * innerWidth),
        y: Math.round(((1 - v.y) / 2) * innerHeight),
      }
    },
  }
}

void main()
